import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { tableAPI } from '../services/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import './ExcelTable.css';

const LOAN_RETURN_STEPS = [
  { amount: 'Return 1 Amount', balance: 'Balance After R1' },
  { amount: 'Return 2 Amount', balance: 'Balance After R2' },
  { amount: 'Return 3 Amount', balance: 'Balance After R3' },
  { amount: 'Return 4 Amount', balance: 'Balance After R4' },
  { amount: 'Return 5 Amount', balance: 'Balance After R5' },
];

const hasLoanValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return true;
  return String(value).trim() !== '';
};

const parseLoanNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value || 0;
  const cleaned = String(value).replace(/,/g, '').replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const updateLoanCellValue = (row, index, value) => {
  if (!Array.isArray(row) || index === -1) {
    return false;
  }
  const normalizedValue = value === '' ? '' : value;
  if (row[index] === normalizedValue) {
    return false;
  }
  row[index] = normalizedValue;
  return true;
};

const recalcLoanRowValues = (row, headerRow) => {
  if (!Array.isArray(row) || !Array.isArray(headerRow)) {
    return false;
  }

  const findIndex = (label) =>
    headerRow.findIndex(
      (header) => header && String(header).toLowerCase() === label.toLowerCase()
    );

  const indices = {
    received: findIndex('Received Amt'),
    charges: findIndex('Charges'),
    totalReturnable: findIndex('Total Returnable'),
    finalReturnAmount: findIndex('Final Return Amount'),
    balance: findIndex('Balance'),
    remarks: findIndex('Remarks'),
  };

  const returnIndices = LOAN_RETURN_STEPS.map((step) => ({
    amount: findIndex(step.amount),
    balance: findIndex(step.balance),
  }));

  const hasBaseValue =
    (indices.received !== -1 && hasLoanValue(row[indices.received])) ||
    (indices.charges !== -1 && hasLoanValue(row[indices.charges]));
  const hasReturnValue =
    returnIndices.some(
      ({ amount }) => amount !== -1 && hasLoanValue(row[amount])
    ) ||
    (indices.finalReturnAmount !== -1 &&
      hasLoanValue(row[indices.finalReturnAmount]));

  if (!hasBaseValue && !hasReturnValue) {
    let cleared = false;
    const derived = [
      indices.totalReturnable,
      indices.balance,
      indices.remarks,
      ...returnIndices.map(({ balance }) => balance),
    ];
    derived.forEach((colIndex) => {
      if (colIndex !== -1 && row[colIndex] !== '' && row[colIndex] !== undefined) {
        cleared = updateLoanCellValue(row, colIndex, '') || cleared;
      }
    });
    return cleared;
  }

  let changed = false;
  const receivedValue =
    indices.received !== -1 ? parseLoanNumber(row[indices.received]) : 0;
  const chargesValue =
    indices.charges !== -1 ? parseLoanNumber(row[indices.charges]) : 0;
  let totalReturnable = Number((receivedValue + chargesValue).toFixed(2));
  if (Math.abs(totalReturnable) < 0.01) {
    totalReturnable = 0;
  }
  if (indices.totalReturnable !== -1) {
    changed =
      updateLoanCellValue(row, indices.totalReturnable, totalReturnable) ||
      changed;
  }

  let runningBalance = totalReturnable;
  let totalReturned = 0;

  returnIndices.forEach(({ amount, balance }) => {
    const hasReturn =
      amount !== -1 &&
      hasLoanValue(row[amount]) &&
      parseLoanNumber(row[amount]) !== 0;
    if (hasReturn) {
      const returnValue = parseLoanNumber(row[amount]);
      totalReturned += returnValue;
      runningBalance -= returnValue;
      runningBalance = Number(runningBalance.toFixed(2));
      const normalizedBalance =
        Math.abs(runningBalance) < 0.01 ? 0 : runningBalance;
      if (balance !== -1) {
        changed =
          updateLoanCellValue(row, balance, normalizedBalance) || changed;
      }
    } else if (balance !== -1 && row[balance] !== '') {
      changed = updateLoanCellValue(row, balance, '') || changed;
    }
  });

  const hasFinalReturn =
    indices.finalReturnAmount !== -1 &&
    hasLoanValue(row[indices.finalReturnAmount]);
  if (hasFinalReturn) {
    const finalReturn = parseLoanNumber(row[indices.finalReturnAmount]);
    totalReturned += finalReturn;
    runningBalance -= finalReturn;
  }

  runningBalance = Number(runningBalance.toFixed(2));
  const normalizedFinalBalance =
    Math.abs(runningBalance) < 0.01 ? 0 : runningBalance;
  if (indices.balance !== -1) {
    changed =
      updateLoanCellValue(row, indices.balance, normalizedFinalBalance) ||
      changed;
  }

  if (indices.remarks !== -1) {
    let remarks = '';
    if (Math.abs(normalizedFinalBalance) < 0.01) {
      remarks =
        totalReturned === 0 && totalReturnable === 0 ? 'Not yet' : 'Cleared';
    } else if (totalReturned === 0) {
      remarks = 'Not yet';
    } else if (normalizedFinalBalance > 0) {
      remarks = 'Pending';
    } else {
      remarks = 'Cleared';
    }
    changed = updateLoanCellValue(row, indices.remarks, remarks) || changed;
  }

  return changed;
};

const recalcLoanTableData = (tableData) => {
  if (!Array.isArray(tableData) || tableData.length <= 1) {
    return { data: tableData, changed: false };
  }

  const headerRow = Array.isArray(tableData[0]) ? tableData[0] : [];
  let changed = false;
  const normalizedData = tableData.map((row, idx) => {
    if (!Array.isArray(row)) {
      return row;
    }
    if (idx === 0) {
      return [...row];
    }
    const newRow = [...row];
    const rowChanged = recalcLoanRowValues(newRow, headerRow);
    if (rowChanged) {
      changed = true;
    }
    return newRow;
  });

  if (changed) {
    return { data: normalizedData, changed: true };
  }

  return { data: tableData, changed: false };
};

const ExcelTable = ({ initialData, currencyColumns = [], customDropdownColumns = {}, fixedHeaders = false, totalColumns = [], tableId = null, columnAverages = {}, tableName = 'Table', onSelectionStatsChange = null }) => {
  // Start with empty data - will be loaded from MySQL
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  
  // Undo/Redo history
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const maxHistorySize = 50;
  const isUndoRedoRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  // Helper function to save state to history
  const saveToHistory = useCallback((currentData) => {
    if (isUndoRedoRef.current) {
      // Don't save history when undoing/redoing
      return;
    }
    
    const dataCopy = JSON.parse(JSON.stringify(currentData));
    const history = historyRef.current;
    const currentIndex = historyIndexRef.current;
    
    // Remove any history after current index (if user made a new change after undo)
    if (currentIndex < history.length - 1) {
      history.splice(currentIndex + 1);
    }
    
    // Add new state to history
    history.push(dataCopy);
    
    // Limit history size
    if (history.length > maxHistorySize) {
      history.shift();
    } else {
      historyIndexRef.current = history.length - 1;
    }
    
    // Update undo/redo button states
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, [maxHistorySize]);
  
  // Update undo/redo button states
  const updateUndoRedoState = useCallback(() => {
    const history = historyRef.current;
    const currentIndex = historyIndexRef.current;
    setCanUndo(currentIndex > 0);
    setCanRedo(currentIndex < history.length - 1);
  }, []);
  
  // Undo function
  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    
    isUndoRedoRef.current = true;
    historyIndexRef.current--;
    const previousState = historyRef.current[historyIndexRef.current];
    if (previousState) {
      setData(previousState);
    }
    updateUndoRedoState();
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);
  }, [updateUndoRedoState]);
  
  // Redo function
  const redo = useCallback(() => {
    const history = historyRef.current;
    if (historyIndexRef.current >= history.length - 1) return;
    
    isUndoRedoRef.current = true;
    historyIndexRef.current++;
    const nextState = history[historyIndexRef.current];
    if (nextState) {
      setData(nextState);
    }
    updateUndoRedoState();
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);
  }, [updateUndoRedoState]);
  
  // Selection state - must be declared before callbacks that use them
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectionRange, setSelectionRange] = useState(null); // { startRow, startCol, endRow, endCol }
  const [isSelecting, setIsSelecting] = useState(false);
  const hasDraggedRef = useRef(false); // Track if user dragged vs clicked
  const [clipboard, setClipboard] = useState(null); // Store copied cells
  const [editingCell, setEditingCell] = useState(null);
  const [selectionStats, setSelectionStats] = useState(null); // Statistics for selected range
  
  // Helper to check if a cell is in selection range
  const isCellInSelection = useCallback((row, col) => {
    if (!selectionRange) {
      return selectedCell?.row === row && selectedCell?.col === col;
    }
    const { startRow, startCol, endRow, endCol } = selectionRange;
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);
    return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
  }, [selectionRange, selectedCell]);

  // Get all cells in current selection
  const getSelectedCells = useCallback(() => {
    if (!selectionRange && !selectedCell) return [];
    if (!selectionRange) {
      return [{ row: selectedCell.row, col: selectedCell.col }];
    }
    const { startRow, startCol, endRow, endCol } = selectionRange;
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);
    const cells = [];
    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        if (r > 0) cells.push({ row: r, col: c }); // Skip header row
      }
    }
    return cells;
  }, [selectionRange, selectedCell]);

  // Calculate statistics for selected range (skip for table5 - Source and Client)
  const calculateSelectionStats = useCallback(() => {
    // Don't calculate stats for table5 (Source and Client)
    if (tableId === 'table5') {
      setSelectionStats(null);
      return;
    }

    if (!data || data.length === 0) {
      setSelectionStats(null);
      return;
    }

    const selectedCells = getSelectedCells();
    if (selectedCells.length === 0) {
      setSelectionStats(null);
      return;
    }

    // Find currency column index from headers (skip for table4 - Transactions, everything is INR)
    const headers = data[0] || [];
    const currencyColumnIndex = tableId !== 'table4' 
      ? headers.findIndex(h => String(h).toLowerCase().trim() === 'currency')
      : -1;

    // Group cells by column, tracking currency for each cell
    const columnData = {};
    selectedCells.forEach(({ row, col }) => {
      if (!columnData[col]) {
        columnData[col] = [];
      }
      const value = data[row]?.[col];
      if (value !== null && value !== undefined && value !== '') {
        // Get currency for this row (skip for table4 - everything is INR)
        let currency = null;
        
        if (tableId !== 'table4' && currencyColumnIndex !== -1) {
          // For other tables, use currency column
          currency = data[row]?.[currencyColumnIndex]
            ? String(data[row][currencyColumnIndex]).toUpperCase().trim()
            : null;
        }
        // For table4, don't track currency (everything is INR)
        
        columnData[col].push({ value, currency, row });
      }
    });

    // Calculate statistics per column, grouped by currency
    const stats = {};
    Object.keys(columnData).forEach(col => {
      const colIndex = parseInt(col);
      const cellData = columnData[col];
      const header = data[0]?.[colIndex] || `Column ${colIndex + 1}`;
      
      if (cellData.length === 0) {
        stats[colIndex] = {
          header,
          count: 0,
          hasNumeric: false
        };
        return;
      }

      // Group by currency (skip for table4 - everything is INR)
      const byCurrency = {};
      const nonNumericCount = [];
      const numericValues = []; // For table4, use simple array
      
      cellData.forEach(({ value, currency }) => {
        // Remove commas and parse
        const cleaned = String(value).replace(/,/g, '').replace(/\s/g, '');
        const num = parseFloat(cleaned);
        if (!isNaN(num) && isFinite(num)) {
          if (tableId === 'table4') {
            // For table4, don't group by currency (everything is INR)
            numericValues.push(num);
          } else {
            // Group by currency (default to 'INR' if no currency column or empty currency)
            const currencyKey = currency || 'INR';
            if (!byCurrency[currencyKey]) {
              byCurrency[currencyKey] = [];
            }
            byCurrency[currencyKey].push(num);
          }
        } else {
          nonNumericCount.push(value);
        }
      });

      const hasNumeric = tableId === 'table4' 
        ? numericValues.length > 0
        : Object.keys(byCurrency).length > 0;
      const totalCount = cellData.length;

      if (hasNumeric) {
        if (tableId === 'table4') {
          // For table4, simple stats without currency breakdown
          const sum = numericValues.reduce((acc, val) => acc + val, 0);
          const avg = sum / numericValues.length;
          const min = Math.min(...numericValues);
          const max = Math.max(...numericValues);
          
          stats[colIndex] = {
            header,
            count: totalCount,
            numericCount: numericValues.length,
            nonNumericCount: nonNumericCount.length,
            sum: sum,
            average: avg,
            min: min,
            max: max,
            hasNumeric: true
            // No byCurrency for table4
          };
        } else {
          // Calculate totals per currency for other tables
          const currencyStats = {};
          let totalSum = 0;
          let totalNumericCount = 0;
          
          Object.keys(byCurrency).forEach(currency => {
            const values = byCurrency[currency];
            const sum = values.reduce((acc, val) => acc + val, 0);
            const avg = sum / values.length;
            const min = Math.min(...values);
            const max = Math.max(...values);
            
            currencyStats[currency] = {
              sum,
              average: avg,
              min,
              max,
              count: values.length
            };
            
            totalSum += sum;
            totalNumericCount += values.length;
          });

          stats[colIndex] = {
            header,
            count: totalCount,
            numericCount: totalNumericCount,
            nonNumericCount: nonNumericCount.length,
            sum: totalSum, // Total sum across all currencies
            average: totalSum / totalNumericCount,
            min: Math.min(...Object.values(byCurrency).flat()),
            max: Math.max(...Object.values(byCurrency).flat()),
            hasNumeric: true,
            byCurrency: currencyStats // Currency breakdown
          };
        }
      } else {
        // Only non-numeric data - show count only
        stats[colIndex] = {
          header,
          count: totalCount,
          hasNumeric: false
        };
      }
    });

    setSelectionStats(stats);
  }, [data, getSelectedCells, tableId]);

  // Watch for selection changes and calculate statistics (debounced for performance)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateSelectionStats();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectionRange, selectedCell, data, calculateSelectionStats]);

  // Expose selection stats to parent component via callback (skip for table5)
  useEffect(() => {
    if (onSelectionStatsChange && tableId !== 'table5') {
      onSelectionStatsChange(selectionStats);
    } else if (onSelectionStatsChange && tableId === 'table5') {
      // Clear stats for table5
      onSelectionStatsChange(null);
    }
  }, [selectionStats, onSelectionStatsChange, tableId]);

  // Save data to API - must be declared before useEffect that uses it
  const saveData = useCallback(async (dataToSave, immediate = true) => {
    // Don't save if no tableId or no data
    if (!tableId || !dataToSave) {
      console.log(`⏭️ [SAVE] Skipping save - no tableId or data`);
      return;
    }
    
    // Validate data is an array
    if (!Array.isArray(dataToSave)) {
      console.error(`❌ [SAVE] Data is not an array:`, typeof dataToSave);
      return;
    }
    
    // Save even if only headers (data.length === 1) - user might have deleted all rows
    // But ensure we have at least headers
    if (dataToSave.length === 0) {
      console.log(`⏭️ [SAVE] Skipping save - empty array`);
      return;
    }

    // Clear any pending debounced saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    const performSave = async () => {
      try {
        setSaving(true);
        console.log(`💾 [SAVE] Saving table ${tableId} to MySQL...`);
        console.log(`💾 [SAVE] Data rows: ${dataToSave.length}`);
        console.log(`💾 [SAVE] First row:`, dataToSave[0]);
        if (dataToSave.length > 1) {
          console.log(`💾 [SAVE] Second row:`, dataToSave[1]);
        }
        
        // Save to MySQL - wait for response
        await tableAPI.save(tableId, dataToSave);
        
        console.log(`✅ [SAVE] Successfully saved table ${tableId} to MySQL with ${dataToSave.length} rows`);
        
        // Also save to localStorage as backup (after successful MySQL save)
        localStorage.setItem(`table_${tableId}`, JSON.stringify(dataToSave));
        console.log(`✅ [SAVE] Also saved to localStorage as backup`);
        
        // Dispatch multiple events to ensure all listeners catch the update
        // 1. Custom event with data for immediate processing
        window.dispatchEvent(new CustomEvent('tableDataUpdated', {
          detail: { tableId, data: dataToSave }
        }));
        
        // 2. Table-specific events for direct listeners (Table3, Table4, Table5)
        if (tableId === 'table3') {
          window.dispatchEvent(new CustomEvent('table3Updated', {
            detail: { tableId, data: dataToSave }
          }));
        } else if (tableId === 'table4') {
          window.dispatchEvent(new CustomEvent('table4Updated', {
            detail: { tableId, data: dataToSave }
          }));
        } else if (tableId === 'table5') {
          window.dispatchEvent(new CustomEvent('table5Updated', {
            detail: { tableId, data: dataToSave }
          }));
        }
        
        // 3. Storage event simulation for cross-tab/cross-component sync
        // This helps components that listen to storage events
        window.dispatchEvent(new StorageEvent('storage', {
          key: `table_${tableId}`,
          newValue: JSON.stringify(dataToSave),
          storageArea: localStorage
        }));
      } catch (error) {
        console.error(`❌ [SAVE] Error saving table ${tableId} data:`, error);
        // Save to localStorage as backup even on error
        try {
          localStorage.setItem(`table_${tableId}`, JSON.stringify(dataToSave));
          console.log(`✅ [SAVE] Saved to localStorage as fallback`);
        } catch (localError) {
          console.error(`❌ [SAVE] Failed to save to localStorage:`, localError);
        }
        throw error; // Re-throw so caller knows save failed
      } finally {
        setSaving(false);
      }
    };

    // Always save immediately - no debouncing for reliability
    // User actions need immediate persistence
    if (immediate) {
      await performSave();
    } else {
      // Only use debounce for automatic saves (not user actions)
      saveTimeoutRef.current = setTimeout(performSave, 1000);
    }
  }, [tableId]);

  // Keyboard shortcut handler with arrow keys and clipboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if we're in an input field, textarea, or contenteditable element
      const target = e.target;
      const isInputElement = target.tagName === 'INPUT' || 
                            target.tagName === 'TEXTAREA' || 
                            target.isContentEditable ||
                            (target.closest && (target.closest('input') || target.closest('textarea') || target.closest('[contenteditable="true"]')));
      
      // Prevent Ctrl+D (bookmark) on Windows Chrome - must be done early when in table
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && !e.shiftKey && !e.altKey) {
        // Only prevent default if we're in the table (not in an input field)
        if (!isInputElement && !editingCell) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
      
      if (isInputElement) {
        // Only handle Escape if editing a cell
        if (editingCell && e.key === 'Escape') {
          setEditingCell(null);
          setEditValue('');
        }
        return;
      }

      // Don't handle if editing a cell (let input handle it)
      if (editingCell) {
        // Allow Escape to cancel editing
        if (e.key === 'Escape') {
          setEditingCell(null);
          setEditValue('');
        }
        return;
      }

      // Arrow key navigation (including numpad keys)
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      const numpadKeys = { '2': 'ArrowDown', '4': 'ArrowLeft', '6': 'ArrowRight', '8': 'ArrowUp' };
      const isArrowKey = arrowKeys.includes(e.key) || (e.key in numpadKeys);
      const effectiveKey = numpadKeys[e.key] || e.key;
      
      if (isArrowKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (!selectedCell) {
          // If no selection, start at first data cell
          if (data.length > 1 && data[0].length > 0) {
            setSelectedCell({ row: 1, col: 0 });
            setSelectionRange(null);
          }
          return;
        }

        let newRow = selectedCell.row;
        let newCol = selectedCell.col;
        const maxRow = data.length - 1;
        const maxCol = (data[0]?.length || 1) - 1;

        if (effectiveKey === 'ArrowUp') {
          newRow = Math.max(1, newRow - 1); // Don't go to header row
        } else if (effectiveKey === 'ArrowDown') {
          newRow = Math.min(maxRow, newRow + 1);
        } else if (effectiveKey === 'ArrowLeft') {
          newCol = Math.max(0, newCol - 1);
        } else if (effectiveKey === 'ArrowRight') {
          newCol = Math.min(maxCol, newCol + 1);
        }

        // If Shift is held, extend selection
        if (e.shiftKey && selectionRange) {
          setSelectionRange({
            ...selectionRange,
            endRow: newRow,
            endCol: newCol
          });
        } else if (e.shiftKey && !selectionRange) {
          // Start new selection range
          setSelectionRange({
            startRow: selectedCell.row,
            startCol: selectedCell.col,
            endRow: newRow,
            endCol: newCol
          });
        } else {
          // Move selection without extending
          setSelectedCell({ row: newRow, col: newCol });
          setSelectionRange(null);
        }

        // Scroll cell into view
        setTimeout(() => {
          const cellElement = document.querySelector(`[data-row="${newRow}"][data-col="${newCol}"]`);
          if (cellElement) {
            cellElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        }, 0);
        return;
      }

      // Ctrl+Z / Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl+Y / Cmd+Shift+Z for redo
      else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
      // Ctrl+C / Cmd+C for copy
      else if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        const selectedCells = getSelectedCells();
        if (selectedCells.length > 0) {
          // Calculate the bounds of the selection
          const rows = selectedCells.map(c => c.row);
          const cols = selectedCells.map(c => c.col);
          const minRow = Math.min(...rows);
          const maxRow = Math.max(...rows);
          const minCol = Math.min(...cols);
          const maxCol = Math.max(...cols);
          
          const copiedData = selectedCells.map(({ row, col }) => ({
            row: row - minRow, // Store relative position
            col: col - minCol, // Store relative position
            value: data[row]?.[col] || ''
          }));
          
          // Store clipboard with dimensions
          setClipboard({
            data: copiedData,
            rows: maxRow - minRow + 1,
            cols: maxCol - minCol + 1
          });
          
          // Also copy to system clipboard
          if (navigator.clipboard && navigator.clipboard.writeText) {
            // Create a grid representation for system clipboard
            const grid = [];
            for (let r = minRow; r <= maxRow; r++) {
              const row = [];
              for (let c = minCol; c <= maxCol; c++) {
                const cell = selectedCells.find(sc => sc.row === r && sc.col === c);
                row.push(cell ? (data[r]?.[c] || '') : '');
              }
              grid.push(row.join('\t'));
            }
            navigator.clipboard.writeText(grid.join('\n')).catch(() => {});
          }
        }
      }
      // Ctrl+V / Cmd+V for paste
      else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        if (clipboard && selectedCell) {
          const newData = [...data];
          saveToHistory(data);
          
          // Get header row to check for Notes and Record columns
          const headerRow = newData[0] || [];
          const isNotesColumn = (colIndex) => {
            const header = headerRow[colIndex];
            return header && String(header).toLowerCase().trim() === 'notes';
          };
          const isRecordColumn = (colIndex) => {
            const header = headerRow[colIndex];
            return header && String(header).toLowerCase().trim() === 'record';
          };
          
          // Handle both old format (array) and new format (object with data, rows, cols)
          let clipboardData = Array.isArray(clipboard) ? clipboard : (clipboard.data || []);
          let clipboardRows = Array.isArray(clipboard) ? null : (clipboard.rows || null);
          let clipboardCols = Array.isArray(clipboard) ? null : (clipboard.cols || null);
          
          if (clipboardData.length === 0) return;
          
          // If we have a selection range, paste into that range
          if (selectionRange) {
            const { startRow, startCol, endRow, endCol } = selectionRange;
            const minRow = Math.min(startRow, endRow);
            const maxRow = Math.max(startRow, endRow);
            const minCol = Math.min(startCol, endCol);
            const maxCol = Math.max(startCol, endCol);
            
            // Calculate dimensions if not provided (old format)
            if (!clipboardRows || !clipboardCols) {
              const rows = clipboardData.map(c => c.row || 0);
              const cols = clipboardData.map(c => c.col || 0);
              clipboardRows = Math.max(...rows) - Math.min(...rows) + 1;
              clipboardCols = Math.max(...cols) - Math.min(...cols) + 1;
            }
            
            // Paste maintaining grid structure
            for (let r = minRow; r <= maxRow; r++) {
              for (let c = minCol; c <= maxCol; c++) {
                if (r > 0 && r < newData.length && c < (newData[0]?.length || 0)) {
                  // Calculate relative position in target range
                  const relRow = r - minRow;
                  const relCol = c - minCol;
                  
                  // Find cell with matching relative position in clipboard
                  const cell = clipboardData.find(clipCell => 
                    (clipCell.row === relRow || (clipCell.row === undefined && relRow === 0)) && 
                    (clipCell.col === relCol || (clipCell.col === undefined && relCol === 0))
                  );
                  
                  if (cell) {
                    let pasteValue = cell.value;
                    // For Notes and Record columns, always treat as string
                    if (isNotesColumn(c) || isRecordColumn(c)) {
                      pasteValue = String(pasteValue || '');
                    }
                    newData[r][c] = pasteValue;
                  }
                }
              }
            }
          } else {
            // Paste starting from selected cell, maintaining grid structure
            const startRow = selectedCell.row;
            const startCol = selectedCell.col;
            
            // Calculate dimensions if not provided (old format)
            if (!clipboardRows || !clipboardCols) {
              if (Array.isArray(clipboard)) {
                // Old format - try to detect grid structure from absolute positions
                const rows = clipboard.map(c => c.row !== undefined ? c.row : 0);
                const cols = clipboard.map(c => c.col !== undefined ? c.col : 0);
                if (rows.length > 0 && cols.length > 0) {
                  const minRow = Math.min(...rows);
                  const minCol = Math.min(...cols);
                  clipboardRows = Math.max(...rows) - minRow + 1;
                  clipboardCols = Math.max(...cols) - minCol + 1;
                  
                  // Convert old format to relative positions
                  clipboardData = clipboard.map(c => ({
                    row: (c.row !== undefined ? c.row : 0) - minRow,
                    col: (c.col !== undefined ? c.col : 0) - minCol,
                    value: c.value
                  }));
                } else {
                  clipboardRows = 1;
                  clipboardCols = clipboardData.length;
                }
              } else {
                clipboardRows = 1;
                clipboardCols = clipboardData.length;
              }
            }
            
            // Paste maintaining grid structure - iterate through grid
            // Ensure we have valid dimensions
            if (clipboardRows && clipboardCols && clipboardRows > 0 && clipboardCols > 0) {
              for (let relRow = 0; relRow < clipboardRows; relRow++) {
                for (let relCol = 0; relCol < clipboardCols; relCol++) {
                  const targetRow = startRow + relRow;
                  const targetCol = startCol + relCol;
                  
                  // Find cell with matching relative position
                  // For new format, cells have explicit row/col relative positions
                  const cell = clipboardData.find(c => 
                    c.row !== undefined && c.col !== undefined && 
                    c.row === relRow && c.col === relCol
                  );
                  
                  if (cell && targetRow > 0 && targetRow < newData.length && targetCol >= 0 && targetCol < (newData[0]?.length || 0)) {
                    let pasteValue = cell.value;
                    // For Notes and Record columns, always treat as string
                    if (isNotesColumn(targetCol) || isRecordColumn(targetCol)) {
                      pasteValue = String(pasteValue || '');
                    }
                    newData[targetRow][targetCol] = pasteValue;
                  }
                }
              }
            } else {
              // Fallback: paste sequentially (old format or single cell)
              let currentRow = startRow;
              let currentCol = startCol;
              
              clipboardData.forEach((cell) => {
                if (currentRow > 0 && currentRow < newData.length && currentCol >= 0 && currentCol < (newData[0]?.length || 0)) {
                  let pasteValue = cell.value;
                  if (isNotesColumn(currentCol) || isRecordColumn(currentCol)) {
                    pasteValue = String(pasteValue || '');
                  }
                  newData[currentRow][currentCol] = pasteValue;
                }
                currentCol++;
                if (currentCol >= (newData[0]?.length || 0)) {
                  currentRow++;
                  currentCol = startCol;
              }
            });
            }
          }
          
          setData(newData);
          if (tableId) {
            setTimeout(() => {
              saveData(newData, true);
            }, 100);
          }
        }
      }
      // Delete or Backspace to clear selected cells
      else if ((e.key === 'Delete' || e.key === 'Backspace') && !editingCell) {
        e.preventDefault();
        const selectedCells = getSelectedCells();
        if (selectedCells.length > 0) {
          const newData = [...data];
          saveToHistory(data);
          let hasChanges = false;
          
          selectedCells.forEach(({ row, col }) => {
            if (row > 0 && row < newData.length && col >= 0 && col < (newData[0]?.length || 0)) {
              if (newData[row][col] !== '') {
                newData[row][col] = '';
                hasChanges = true;
              }
            }
          });
          
          if (hasChanges) {
            setData(newData);
            if (tableId) {
              setTimeout(() => {
                saveData(newData, true);
              }, 100);
            }
          }
        }
      }
      // Ctrl+D / Cmd+D for fill down
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        if (selectedCell && data.length > 0 && data[0]?.length > 0) {
          const newData = data.map(row => [...row]); // Deep copy
            saveToHistory(data);
          let hasChanges = false;
          
          if (selectionRange) {
            // Fill down for selection range: copy from first row to all rows below in the range
            const { startRow, startCol, endRow, endCol } = selectionRange;
            const minRow = Math.min(startRow, endRow);
            const maxRow = Math.max(startRow, endRow);
            const minCol = Math.min(startCol, endCol);
            const maxCol = Math.max(startCol, endCol);
            
            // Fill down: copy from first row of selection to all rows below
            if (minRow >= 0 && maxRow > minRow && maxRow < newData.length) {
              for (let r = minRow + 1; r <= maxRow; r++) {
                for (let c = minCol; c <= maxCol; c++) {
                  if (r < newData.length && c < (newData[0]?.length || 0) && minRow < newData.length) {
                    const sourceValue = newData[minRow][c];
                    if (newData[r][c] !== sourceValue) {
                      newData[r][c] = sourceValue;
                      hasChanges = true;
                    }
                  }
                }
              }
            }
          } else {
            // Fill down from cell above (single cell)
            // In Excel, Ctrl+D copies from the cell directly above
            if (selectedCell.row > 0 && selectedCell.row < newData.length) {
              const sourceRow = selectedCell.row - 1;
              if (sourceRow >= 0 && sourceRow < newData.length && selectedCell.col < (newData[0]?.length || 0)) {
                const sourceValue = newData[sourceRow][selectedCell.col];
                if (newData[selectedCell.row][selectedCell.col] !== sourceValue) {
          newData[selectedCell.row][selectedCell.col] = sourceValue;
                  hasChanges = true;
                }
              }
            }
          }
          
          if (hasChanges) {
          setData(newData);
          if (tableId) {
            setTimeout(() => {
              saveData(newData, true);
            }, 100);
            }
          }
        }
      }
      // Enter or F2 to start editing
      else if (e.key === 'Enter' || e.key === 'F2') {
        e.preventDefault();
        const isCustomDropdownCol = customDropdownColumns && customDropdownColumns[selectedCell?.col];
        if (selectedCell && !currencyColumns.includes(selectedCell.col) && !isCustomDropdownCol) {
          setEditingCell(selectedCell);
          // Parse value (remove commas) when opening edit mode
          const cellValue = data[selectedCell.row]?.[selectedCell.col] || '';
          setEditValue(parseValueFromDisplay(cellValue, editingCell.col));
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo, selectedCell, selectionRange, clipboard, editingCell, data, tableId, saveData, currencyColumns, getSelectedCells, saveToHistory, isCellInSelection]);
  
  // Initialize history with initial data when data is first loaded
  useEffect(() => {
    if (data.length > 0 && historyRef.current.length === 0 && !loading) {
      const dataCopy = JSON.parse(JSON.stringify(data));
      historyRef.current = [dataCopy];
      historyIndexRef.current = 0;
      setCanUndo(false);
      setCanRedo(false);
    }
  }, [data.length, loading]);

  // Track if we've loaded data for this table
  const hasLoadedRef = useRef(false);
  const loadedTableIdRef = useRef(null);
  const hasScrolledToBottomRef = useRef(false);

  // Load data from API if tableId is provided (only once per tableId)
  const loadData = useCallback(async () => {
    // Table6 (Stock Balance) is calculated from table3 and table4, not stored in MySQL
    // Use the calculated initialData instead
    if (tableId === 'table6') {
      if (initialData && initialData.length > 0) {
        console.log(`📊 [TABLE6] Using calculated initialData (${initialData.length} rows)`);
        setData(initialData);
        localStorage.setItem(`table_${tableId}`, JSON.stringify(initialData));
      } else {
        setData([['S no', 'Buying Rate', 'Total Amount', 'Currency', 'Notes']]);
      }
      setLoading(false);
      hasLoadedRef.current = true;
      return;
    }
    
    if (!tableId) {
      if (!hasLoadedRef.current) {
        setData(initialData || []);
        setLoading(false);
        hasLoadedRef.current = true;
      }
      return;
    }

    // COMPLETELY RECODED: Always load on mount (page refresh = new mount)
    // The hasLoadedRef check is handled in the useEffect above
    console.log(`📥 [LOAD] Proceeding with load for table ${tableId}...`);

    try {
      setLoading(true);
      console.log(`📥 [LOAD] Starting load for table ${tableId}...`);
      console.log(`📥 [LOAD] hasLoadedRef.current:`, hasLoadedRef.current);
      console.log(`📥 [LOAD] loadedTableIdRef.current:`, loadedTableIdRef.current);
      
      const savedData = await tableAPI.getById(tableId);
      
      console.log(`📥 [LOAD] API Response received:`, savedData);
      console.log(`📥 [LOAD] Response is array:`, Array.isArray(savedData));
      console.log(`📥 [LOAD] Response length:`, Array.isArray(savedData) ? savedData.length : 'N/A');
      console.log(`📥 [LOAD] Response type:`, typeof savedData);
      
      let dataToSet = [];
      
      if (savedData && Array.isArray(savedData) && savedData.length > 0) {
        // Data from MySQL - use it
        let processedData = savedData;
        
        // For table4, remove Name column if it exists and ensure correct column order
        if (tableId === 'table4' && processedData.length > 0) {
          const headers = processedData[0];
          if (Array.isArray(headers)) {
            let needsSave = false;
            
            // Remove Name column if it exists
            const nameIndex = headers.findIndex(h => String(h).toLowerCase() === 'name');
            if (nameIndex !== -1) {
              // Remove Name column from headers
              const newHeaders = headers.filter((_, idx) => idx !== nameIndex);
              processedData[0] = newHeaders;
              
              // Remove Name column from all data rows
              for (let i = 1; i < processedData.length; i++) {
                if (Array.isArray(processedData[i])) {
                  processedData[i] = processedData[i].filter((_, idx) => idx !== nameIndex);
                }
              }
              needsSave = true;
            }
            
            // ALWAYS ensure correct column order: INR, Buying Rate, Stock INR
            // Replace header row with correct order from initialData
            if (initialData && initialData.length > 0 && Array.isArray(initialData[0])) {
              const correctHeaders = initialData[0];
              const currentHeaders = processedData[0];
              
              // Check if headers match the correct order
              const inrIndex = currentHeaders.findIndex(h => String(h).toLowerCase() === 'inr');
              const stockInrIndex = currentHeaders.findIndex(h => String(h).toLowerCase() === 'stock inr');
              const correctInrIndex = correctHeaders.findIndex(h => String(h).toLowerCase() === 'inr');
              const correctStockInrIndex = correctHeaders.findIndex(h => String(h).toLowerCase() === 'stock inr');
              
              // Always reorder if positions don't match OR if header row doesn't exactly match
              const headersMatch = currentHeaders.length === correctHeaders.length &&
                currentHeaders.every((h, idx) => String(h).toLowerCase() === String(correctHeaders[idx]).toLowerCase());
              
              if (!headersMatch || (inrIndex !== -1 && stockInrIndex !== -1 && 
                  correctInrIndex !== -1 && correctStockInrIndex !== -1 &&
                  (inrIndex !== correctInrIndex || stockInrIndex !== correctStockInrIndex))) {
                
                console.log(`🔄 [MIGRATION] Reordering table4 columns to match correct order`);
                console.log(`   Current headers:`, currentHeaders);
                console.log(`   Correct headers:`, correctHeaders);
                
                // Create a mapping from old indices to new indices
                const columnMapping = {};
                correctHeaders.forEach((correctHeader, newIdx) => {
                  const oldIdx = currentHeaders.findIndex(h => 
                    String(h).toLowerCase() === String(correctHeader).toLowerCase()
                  );
                  if (oldIdx !== -1) {
                    columnMapping[oldIdx] = newIdx;
                  }
                });
              
                // Reorder all rows based on the mapping
                const reorderedData = processedData.map((row, rowIdx) => {
                  if (!Array.isArray(row)) return row;
                  const newRow = new Array(correctHeaders.length).fill('');
                  Object.keys(columnMapping).forEach(oldIdx => {
                    const newIdx = columnMapping[oldIdx];
                    if (row[oldIdx] !== undefined) {
                      newRow[newIdx] = row[oldIdx];
                    }
                  });
                  return newRow;
                });
                
                // Replace headers with correct order
                reorderedData[0] = [...correctHeaders];
                processedData = reorderedData;
                needsSave = true;
              }
            }
            
            // Save updated data back to MySQL if changes were made
            if (needsSave) {
              try {
                await saveData(processedData, true);
                console.log(`✅ [LOAD] Migrated table4 - column order corrected`);
              } catch (error) {
                console.error('Error saving migrated data:', error);
              }
            }
          }
        }
        
        // For table5, check if Type column exists and add it if missing
        if (tableId === 'table5' && processedData.length > 0) {
          const headers = processedData[0];
          if (Array.isArray(headers) && headers.indexOf('Type') === -1) {
            // Type column doesn't exist - add it after Name column
            const nameIndex = headers.indexOf('Name');
            const insertIndex = nameIndex !== -1 ? nameIndex + 1 : 1;
            
            // Add Type header
            headers.splice(insertIndex, 0, 'Type');
            
            // Add empty Type value to all data rows
            for (let i = 1; i < processedData.length; i++) {
              if (Array.isArray(processedData[i])) {
                processedData[i].splice(insertIndex, 0, '');
              }
            }
            
            // Save updated data back to MySQL
            try {
              await saveData(processedData, true);
              console.log(`✅ [LOAD] Added Type column to table5`);
            } catch (error) {
              console.error('Error saving migrated data:', error);
            }
          }
        }
        
        // For table7_moneyin and table7_moneyout, add Record column after Code if missing
        if ((tableId === 'table7_moneyin' || tableId === 'table7_moneyout') && processedData.length > 0) {
          const headers = processedData[0];
          if (Array.isArray(headers)) {
            const recordIndex = headers.findIndex(h => String(h).toLowerCase() === 'record');
            const codeIndex = headers.findIndex(h => String(h).toLowerCase() === 'code');
            
            // If Record column is missing and Code exists, add it after Code
            if (recordIndex === -1 && codeIndex !== -1) {
              const insertIndex = codeIndex + 1;
              
              console.log(`🔄 [MIGRATION] Adding Record column to ${tableId} after Code column`);
              
              // Add Record header
              headers.splice(insertIndex, 0, 'Record');
              
              // Add empty Record value to all data rows
              for (let i = 1; i < processedData.length; i++) {
                if (Array.isArray(processedData[i])) {
                  processedData[i].splice(insertIndex, 0, '');
                }
              }
              
              // Save updated data back to MySQL
              try {
                await saveData(processedData, true);
                console.log(`✅ [LOAD] Added Record column to ${tableId}`);
              } catch (error) {
                console.error('Error saving migrated data:', error);
              }
            }
          }
        }
        
        if (tableId === 'table7' || tableId === 'table7_moneyin' || tableId === 'table7_moneyout') {
          const { data: normalizedData, changed } = recalcLoanTableData(processedData);
          if (changed) {
            processedData = normalizedData;
            try {
              await saveData(processedData, true);
              console.log(`✅ [LOAD] Normalized loan table structure for ${tableId}`);
            } catch (error) {
              console.error('Error saving normalized loan data:', error);
            }
          }
        }
        
        console.log(`✅ [LOAD] Setting data with ${processedData.length} rows`);
        console.log(`📊 [LOAD] First row:`, processedData[0]);
        console.log(`📊 [LOAD] Second row:`, processedData[1]);
        dataToSet = processedData;
      } else {
        console.log(`⚠️ [LOAD] No valid data found - savedData:`, savedData);
        // No data in MySQL - show empty table with headers only
        // Only use initialData if it's exactly 1 row (just headers), never if it has data rows
        if (initialData && initialData.length === 1 && Array.isArray(initialData[0]) && initialData[0].length > 0) {
          // Only headers - safe to use
          dataToSet = initialData;
        } else if (initialData && initialData.length > 1) {
          // Has data rows (might be sample data) - extract just the header row
          const headersOnly = initialData.slice(0, 1);
          dataToSet = headersOnly;
        } else {
          // No valid headers - show empty
          dataToSet = [];
        }

        if ((tableId === 'table7' || tableId === 'table7_moneyin' || tableId === 'table7_moneyout') && Array.isArray(dataToSet) && dataToSet.length > 0) {
          const { data: normalizedData } = recalcLoanTableData(dataToSet);
          dataToSet = normalizedData;
        }
      }

      setData(dataToSet);
      
      hasLoadedRef.current = true;
      loadedTableIdRef.current = tableId;
    } catch (error) {
      console.error('Error loading table data:', error);
      // Don't fallback to localStorage or sample data - only use MySQL
      // If MySQL fails, show empty table
      if (initialData && initialData.length === 1 && Array.isArray(initialData[0])) {
        // Only headers - safe to use
        setData(initialData);
      } else {
        // No data - show empty
        setData([]);
      }
      hasLoadedRef.current = true;
      loadedTableIdRef.current = tableId;
    } finally {
      setLoading(false);
    }
  }, [tableId, initialData]);

  // Update data when initialData changes (for calculated tables like Table6)
  useEffect(() => {
    // For table6 (Stock Balance), always use the calculated initialData
    // This ensures it updates when table3 or table4 changes
    if (tableId === 'table6' && initialData && initialData.length > 0) {
      console.log(`🔄 [TABLE6] Updating data from calculated initialData (${initialData.length} rows)`);
      setData(initialData);
      // Also save to localStorage for consistency
      localStorage.setItem(`table_${tableId}`, JSON.stringify(initialData));
    }
  }, [initialData, tableId]);

  // Auto-scroll to last date row when table is first opened/loaded
  useEffect(() => {
    // Only scroll on initial load (when data is first loaded for this tableId)
    // Check if this is a new table load (tableId changed) or first load
    const isNewTableLoad = loadedTableIdRef.current === tableId && hasLoadedRef.current && !hasScrolledToBottomRef.current;
    
    if (isNewTableLoad && data.length > 1 && tableWrapperRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (loadedTableIdRef.current === tableId) {
          // Use the existing scrollToLastDate function to scroll to last date row
          scrollToLastDate();
          hasScrolledToBottomRef.current = true;
        }
      }, 200);
    }
  }, [data, tableId]);

  // Reset scroll flag when tableId changes (new table opened)
  useEffect(() => {
    hasScrolledToBottomRef.current = false;
  }, [tableId]);

  // Initialize: Load data from API on mount - COMPLETELY RECODED
  useEffect(() => {
    console.log(`🔄 [MOUNT] ExcelTable mounted/updated for tableId: ${tableId}`);
    
    // ALWAYS reset load flag on mount (page refresh = new mount)
    // This ensures we always load data on page refresh
    hasLoadedRef.current = false;
    loadedTableIdRef.current = null;
    hasScrolledToBottomRef.current = false;
    
    // Always load data on mount
    console.log(`🔄 [MOUNT] Calling loadData() for tableId: ${tableId}...`);
    loadData();
  }, [tableId]); // Only depend on tableId - loadData is stable

  // Save data when it changes (but don't trigger on initial load)
  // DISABLED: User actions trigger immediate saves, so we don't need this
  // This prevents saving stale data when state updates
  const isInitialMount = useRef(true);
  
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // DO NOT auto-save on data changes anymore
    // All user actions (add row, edit cell, etc.) already trigger immediate saves
    // Auto-saving here causes issues with stale data
    
  }, [data, saveData, loading]);
  const [editingHeader, setEditingHeader] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [headerEditValue, setHeaderEditValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(null);
  const [showAddCurrencyModal, setShowAddCurrencyModal] = useState(false);
  const [showAddRowsModal, setShowAddRowsModal] = useState(false);
  const [newCurrency, setNewCurrency] = useState('');
  const [rowsToAdd, setRowsToAdd] = useState('1');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const [customCurrencies, setCustomCurrencies] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const [fillRange, setFillRange] = useState(null);
  const [isDraggingFill, setIsDraggingFill] = useState(false);
  const [fillStartCell, setFillStartCell] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [filters, setFilters] = useState({});
  const [showFilterInput, setShowFilterInput] = useState(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0, width: 200 });
  const filterInputRef = useRef(null);
  const filterButtonRefs = useRef({});
  const inputRef = useRef(null);
  
  // Update filter position when window scrolls or resizes
  useEffect(() => {
    if (showFilterInput !== null && filterButtonRefs.current[showFilterInput]) {
      const updatePosition = () => {
        const button = filterButtonRefs.current[showFilterInput];
        if (button) {
          const buttonRect = button.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Calculate optimal position - ensure it's not cut off
          let leftPos = buttonRect.left;
          let topPos = buttonRect.bottom + 4;
          const filterWidth = 250;
          
          // If filter would go off right edge, position it to the left of button
          if (leftPos + filterWidth > viewportWidth - 10) {
            leftPos = Math.max(10, buttonRect.right - filterWidth);
          }
          
          // If filter would go off bottom, position it above button
          if (topPos + 60 > viewportHeight - 10) {
            topPos = Math.max(10, buttonRect.top - 60);
          }
          
          setFilterPosition({
            top: topPos,
            left: leftPos,
            width: filterWidth
          });
        }
      };
      
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showFilterInput]);
  const dropdownRef = useRef(null);
  const currencyInputRef = useRef(null);
  const rowsInputRef = useRef(null);
  const tableWrapperRef = useRef(null);
  
  // Track initial row and column counts (fixed/original)
  const initialRowCount = useRef(initialData.length);
  const initialColCount = useRef(initialData[0]?.length || 0);

  const rows = data.length;
  const cols = data[0]?.length || 0;

  // Check if a column is a date column
  const isDateColumn = (colIndex) => {
    const header = data[0]?.[colIndex];
    return header && header.toLowerCase() === 'date';
  };

  const loadSourceClientTable = () => {
    try {
      const sourceClientData = localStorage.getItem('table_table5');
      if (!sourceClientData) return null;
      
      const parsed = JSON.parse(sourceClientData);
      if (!parsed || parsed.length < 2) return null;
      return parsed;
    } catch (e) {
      console.error('Error loading Source and Client data:', e);
      return null;
    }
  };

  // Load code-to-name mapping from Source and Client table
  const getCodeToNameMap = () => {
    const parsed = loadSourceClientTable();
    if (!parsed) return {};
      
      const headers = parsed[0];
      const nameIndex = headers.indexOf('Name');
      const codeIndex = headers.indexOf('Code');
      
      if (nameIndex === -1 || codeIndex === -1) return {};
      
      const codeToNameMap = {};
      parsed.slice(1).forEach(row => {
        if (row && row[codeIndex] && row[nameIndex]) {
          const code = String(row[codeIndex]).trim();
          const name = String(row[nameIndex]).trim();
          if (code && name) {
            // Store both original case and uppercase for case-insensitive lookup
            codeToNameMap[code] = name;
            codeToNameMap[code.toUpperCase()] = name;
            codeToNameMap[code.toLowerCase()] = name;
          }
        }
      });
      
      return codeToNameMap;
  };

  const getNameToCodeMap = () => {
    const parsed = loadSourceClientTable();
    if (!parsed) return {};
    
    const headers = parsed[0];
    const nameIndex = headers.indexOf('Name');
    const codeIndex = headers.indexOf('Code');
    
    if (nameIndex === -1 || codeIndex === -1) return {};
    
    const nameToCodeMap = {};
    parsed.slice(1).forEach(row => {
      if (row && row[codeIndex] && row[nameIndex]) {
        const code = String(row[codeIndex]).trim();
        const name = String(row[nameIndex]).trim();
        if (code && name) {
          nameToCodeMap[name] = code;
          nameToCodeMap[name.toLowerCase()] = code;
        }
      }
    });
    
    return nameToCodeMap;
  };

  // Parse date from dd/mm/yyyy format
  const parseDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const parts = dateString.trim().split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    return new Date(year, month - 1, day);
  };

  // Sort data based on selected column
  const getSortedData = () => {
    if (sortColumn === null) {
      // Return data with original row indices attached
      return data.map((row, idx) => ({ row, originalIndex: idx }));
    }
    
    const headerRow = data[0];
    const dataRows = data.slice(1).map((row, idx) => ({ row, originalIndex: idx + 1 }));
    
    const isDate = isDateColumn(sortColumn);
    
    dataRows.sort((a, b) => {
      const aVal = a.row[sortColumn] || '';
      const bVal = b.row[sortColumn] || '';
      
      if (isDate) {
        const aDate = parseDate(aVal);
        const bDate = parseDate(bVal);
        
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        
        const comparison = aDate.getTime() - bDate.getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        // Try numeric comparison first
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          const comparison = aNum - bNum;
          return sortDirection === 'asc' ? comparison : -comparison;
        }
        
        // String comparison
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });
    
    return [{ row: headerRow, originalIndex: 0 }, ...dataRows];
  };

  // Filter data based on filter values
  const getFilteredData = (sortedData) => {
    const hasFilters = Object.keys(filters).some(col => filters[col] && filters[col].trim() !== '');
    if (!hasFilters) return sortedData;
    
    const headerRow = sortedData[0];
    const dataRows = sortedData.slice(1);
    
    const filteredRows = dataRows.filter(item => {
      return Object.keys(filters).every(colIndex => {
        const filterValue = filters[colIndex];
        if (!filterValue || filterValue.trim() === '') return true;
        
        const cellValue = String(item.row[colIndex] || '').toLowerCase();
        const searchValue = filterValue.toLowerCase();
        
        return cellValue.includes(searchValue);
      });
    });
    
    return [headerRow, ...filteredRows];
  };

  const handleSort = (colIndex) => {
    if (sortColumn === colIndex) {
      // If already sorted by this column, cycle: asc -> desc -> unsort
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        // Unsort - clear the sort
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      // Sort by new column
      setSortColumn(colIndex);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (colIndex, value) => {
    setFilters(prev => ({
      ...prev,
      [colIndex]: value
    }));
  };

  const clearFilter = (colIndex) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[colIndex];
      return newFilters;
    });
    setShowFilterInput(null);
  };

  // Preserve scroll position across re-renders
  const scrollPositionRef = useRef({ scrollLeft: 0, scrollTop: 0 });
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  
  // Save scroll position as user scrolls
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const handleScroll = () => {
      isUserScrollingRef.current = true;
      scrollPositionRef.current = {
        scrollLeft: wrapper.scrollLeft,
        scrollTop: wrapper.scrollTop
      };
      
      // Reset flag after scroll ends
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 150);
    };

    wrapper.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Get sorted and filtered data for display - memoized to prevent unnecessary recalculations
  const sortedData = useMemo(() => getSortedData(), [data, sortColumn, sortDirection]);
  const displayData = useMemo(() => getFilteredData(sortedData), [sortedData, filters]);

  // Restore scroll position after data changes, but only if user wasn't actively scrolling
  useEffect(() => {
    // Don't restore if user is actively scrolling
    if (isUserScrollingRef.current) return;
    
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const { scrollLeft, scrollTop } = scrollPositionRef.current;
    
    // Only restore if position has changed from what we saved
    const restoreScroll = () => {
      if (wrapper && !isUserScrollingRef.current) {
        // Check if current position differs from saved position
        if (Math.abs(wrapper.scrollLeft - scrollLeft) > 1 || Math.abs(wrapper.scrollTop - scrollTop) > 1) {
          wrapper.scrollLeft = scrollLeft;
          wrapper.scrollTop = scrollTop;
        }
      }
    };

    // Restore after a short delay to ensure DOM is updated
    const timeoutId = setTimeout(restoreScroll, 0);
    requestAnimationFrame(restoreScroll);
    
    return () => clearTimeout(timeoutId);
  }, [data, displayData]);
  const currencyOptions = ['INR', 'AED', ...customCurrencies];
  const usesCurrencyTotals = tableId === 'table2' || tableId === 'table1';

  // Indian number formatting function
  const formatIndianNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    
    // Convert to string and remove any existing commas/spaces
    const str = String(value).trim().replace(/,/g, '').replace(/\s/g, '');
    
    // Check if it's a number (including negative and decimal)
    const numMatch = str.match(/^-?\d+(\.\d+)?$/);
    if (!numMatch) return value; // Return original if not a number
    
    // Extract number parts
    const isNegative = str.startsWith('-');
    const numStr = str.replace(/^-/, '');
    const parts = numStr.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    
    // Apply Indian number system: First 3 digits, then groups of 2
    // Example: 1234567 -> 12,34,567
    let formattedInteger = '';
    const len = integerPart.length;
    
    if (len <= 3) {
      formattedInteger = integerPart;
    } else {
      // First 3 digits from right (no comma before them)
      formattedInteger = integerPart.slice(-3);
      // Remaining digits in groups of 2 from right to left
      let remaining = integerPart.slice(0, -3);
      while (remaining.length > 0) {
        const group = remaining.slice(-2);
        formattedInteger = group + ',' + formattedInteger;
        remaining = remaining.slice(0, -2);
      }
    }
    
    // Combine with decimal part if exists
    const result = decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    return isNegative ? `-${result}` : result;
  };

  // Check if a column is an amount/money column
  const isAmountColumn = (colIndex) => {
    if (!data || !data[0] || !Array.isArray(data[0])) return false;
    const header = String(data[0][colIndex] || '').toLowerCase().trim();
    
    // Check if header contains amount/money keywords
    const amountKeywords = ['amount', 'amt', 'inr', 'aed', 'profit', 'expense', 'rate', 'money', 'price', 'cost', 'total', 'stock', 'buying', 'selling', 'cross', 'net', 'returnable', 'return', 'balance', 'charges'];
    const isAmountHeader = amountKeywords.some(keyword => header.includes(keyword));
    
    // Also check if it's adjacent to a currency column (next column is currency dropdown)
    const isAdjacentToCurrency = currencyColumns.some(cc => Math.abs(cc - colIndex) === 1);
    
    // Check if it's a numeric column (total columns or columns that typically have numbers)
    const isNumericColumn = totalColumns.includes(colIndex);
    
    // Don't format SNO, Code, or Date columns
    const isExcluded = header.includes('s no') || header.includes('sno') || header === 'code' || header === 'date' || header === 'name' || header === 'purpose' || header === 'number' || header === 'notes';
    
    return !isExcluded && (isAmountHeader || isAdjacentToCurrency || isNumericColumn);
  };

  // Format value for display (add commas)
  const formatValueForDisplay = (value, colIndex) => {
    // Check if this is a Notes or Record column - return as-is without any formatting
    const headerRow = data[0] || [];
    const header = headerRow[colIndex];
    const isNotesColumn = header && String(header).toLowerCase().trim() === 'notes';
    const isRecordColumn = header && String(header).toLowerCase().trim() === 'record';
    
    // For Notes and Record columns, always return as string without any formatting
    if (isNotesColumn || isRecordColumn) {
      return String(value || '');
    }
    
    if (!isAmountColumn(colIndex)) return value;
    return formatIndianNumber(value);
  };

  // Parse value from display (remove commas for editing/saving)
  const parseValueFromDisplay = (value, colIndex) => {
    // Check if this is a Notes or Record column - preserve everything as-is
    const headerRow = data[0] || [];
    const header = headerRow[colIndex];
    const isNotesColumn = header && String(header).toLowerCase().trim() === 'notes';
    const isRecordColumn = header && String(header).toLowerCase().trim() === 'record';
    const isNameColumn = header && String(header).toLowerCase().trim() === 'name';
    const isPurposeColumn = header && String(header).toLowerCase().trim() === 'purpose';
    
    // For Notes and Record columns, always return as string without any processing
    if (isNotesColumn || isRecordColumn) {
      return String(value || '');
    }
    
    if (typeof value !== 'string') return value;
    
    if (isNameColumn || isPurposeColumn) {
      // For Name and Purpose columns, only remove commas (preserve spaces)
      return value.replace(/,/g, '');
    }
    
    // For other columns (amounts, numbers), remove commas and spaces
    return value.replace(/,/g, '').replace(/\s/g, '');
  };

  const getOrderedCurrencies = (currencyTotals) => {
    if (!currencyTotals) return [];
    const priorityCurrencies = ['AED', 'INR'];
    const ordered = [];
    priorityCurrencies.forEach(currency => {
      if (currencyTotals[currency] !== undefined) {
        ordered.push(currency);
      }
    });
    const remaining = Object.keys(currencyTotals)
      .filter(currency => !ordered.includes(currency))
      .sort();
    return [...ordered, ...remaining];
  };

  const getCurrencyColor = (currency, value) => {
    if (currency === 'INR') return '#dc2626';
    if (currency === 'AED') return '#16a34a';
    return value >= 0 ? '#16a34a' : '#dc2626';
  };
  
  // Calculate totals for specified columns (using displayData for sorted results)
  const calculateTotals = () => {
    const totals = {};
    const headerRow = displayData[0]?.row || [];
    const isSnoColumn = (colIndex) => {
      if (colIndex !== 0) return false;
      const header = headerRow[colIndex];
      if (!header) return false;
      const headerLower = String(header).trim().toLowerCase();
      return headerLower === 's no' || headerLower === 'sno' || headerLower === 's no.';
    };
    
    // For tables using currency-specific totals (A1/table1 and Expense List/table2)
    if (usesCurrencyTotals) {
      const currencyColIndex = currencyColumns[0];
      if (currencyColIndex !== undefined) {
        const totalsByCurrency = {};
        
        displayData.slice(1).forEach(item => {
          const currency = String(item.row[currencyColIndex] || '').trim().toUpperCase();
          if (!currency) return;
          
          if (!totalsByCurrency[currency]) {
            totalsByCurrency[currency] = {};
          }
          
          totalColumns.forEach(colIndex => {
            const value = item.row[colIndex];
            if (value !== undefined && value !== null && value !== '') {
              const numValue = parseFloat(value.toString().replace(/[^0-9.-]/g, ''));
              if (!isNaN(numValue)) {
                totalsByCurrency[currency][colIndex] = (totalsByCurrency[currency][colIndex] || 0) + numValue;
              }
            }
          });
        });
        
        totals._currencyTotals = totalsByCurrency;
        
        const primaryCurrency = 'INR';
        totalColumns.forEach(colIndex => {
          const primaryValue = totalsByCurrency[primaryCurrency]?.[colIndex];
          totals[colIndex] = primaryValue !== undefined ? primaryValue : 0;
        });
      }
    } else {
      // For other tables, calculate normally
      totalColumns.forEach(colIndex => {
        // For Sno column, count the number of data rows instead of summing
        if (isSnoColumn(colIndex)) {
          // Count non-empty rows (rows that have at least one non-empty cell)
          const rowCount = displayData.slice(1).filter(item => {
            return item.row.some(cell => cell !== '' && cell !== null && cell !== undefined);
          }).length;
          totals[colIndex] = rowCount;
        } else {
          let sum = 0;
          displayData.slice(1).forEach(item => {
            const value = item.row[colIndex];
            if (value) {
              // Remove currency symbols and commas, then parse
              const numValue = parseFloat(value.toString().replace(/[^0-9.-]/g, ''));
              if (!isNaN(numValue)) {
                sum += numValue;
              }
            }
          });
          totals[colIndex] = sum;
        }
      });
    }
    return totals;
  };
  
  const totals = calculateTotals();
  
  // Check if a row/column is deletable (added after initial load)
  const isRowDeletable = (rowIndex) => {
    // Row index 0 is header, so data rows start from index 1
    // Original data rows are from index 1 to initialRowCount.current - 1
    return rowIndex >= initialRowCount.current;
  };
  
  const isColumnDeletable = (colIndex) => {
    // S no/Sno/s no column (index 0) should never be deletable
    if (isSnoColumnCheck(colIndex, data[0]?.[colIndex])) return false;
    return colIndex >= initialColCount.current;
  };

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  useEffect(() => {
    if (editingHeader && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingHeader]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside dropdown or dropdown option
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      // Don't close if clicking on dropdown arrow
      if (event.target.closest && event.target.closest('.dropdown-arrow')) {
        return;
      }
      // Close dropdown if clicking outside
      setShowDropdown(null);
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  useEffect(() => {
    if (showAddCurrencyModal && currencyInputRef.current) {
      currencyInputRef.current.focus();
    }
  }, [showAddCurrencyModal]);

  // Handle clicking outside export menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  useEffect(() => {
    if (showAddRowsModal && rowsInputRef.current) {
      rowsInputRef.current.focus();
      rowsInputRef.current.select();
    }
  }, [showAddRowsModal]);

  const getColumnWidthsStorageKey = useCallback(() => {
    if (!tableId) return null;
    return `table_${tableId}_columnWidths`;
  }, [tableId]);

  // Initialize/load column widths (persisted per table)
  useEffect(() => {
    if (cols === 0) {
      setColumnWidths({});
      return;
    }

    const storageKey = getColumnWidthsStorageKey();
    let savedWidths = null;

    if (storageKey) {
      try {
        const storedValue = localStorage.getItem(storageKey);
        if (storedValue) {
          savedWidths = JSON.parse(storedValue);
        }
      } catch (error) {
        console.error('Error loading saved column widths:', error);
      }
    }

    setColumnWidths((prev) => {
      const next = {};
      let changed = false;

      for (let i = 0; i < cols; i++) {
        const hasSavedValue =
          savedWidths && Object.prototype.hasOwnProperty.call(savedWidths, i);
        const savedValue = hasSavedValue ? savedWidths[i] : null;
        const normalizedValue =
          typeof savedValue === 'number'
            ? savedValue
            : (prev && typeof prev[i] === 'number' ? prev[i] : null);
        next[i] = normalizedValue;
        if (!prev || prev[i] !== normalizedValue) {
          changed = true;
        }
      }

      const prevKeys = prev ? Object.keys(prev) : [];
      if (prevKeys.length !== Object.keys(next).length) {
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [cols, getColumnWidthsStorageKey]);

  // Persist column width changes
  useEffect(() => {
    if (!tableId || cols === 0) return;
    if (!columnWidths || Object.keys(columnWidths).length === 0) return;
    const storageKey = getColumnWidthsStorageKey();
    if (!storageKey) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    } catch (error) {
      console.error('Error saving column widths:', error);
    }
  }, [columnWidths, tableId, cols, getColumnWidthsStorageKey]);

  // Handle column resize - completely rewritten for better reliability
  useEffect(() => {
    if (resizingColumn === null) return;

    const handleMouseMove = (e) => {
      if (resizingColumn === null) return;
      
      e.preventDefault();
      const diff = e.clientX - resizeStartXRef.current;
      const newWidth = Math.max(30, resizeStartWidthRef.current + diff); // Minimum width of 30px
      
      setColumnWidths(prev => {
        const updated = { ...prev };
        updated[resizingColumn] = newWidth;
        return updated;
      });
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      setResizingColumn(null);
      resizeStartXRef.current = 0;
      resizeStartWidthRef.current = 0;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };

    // Use capture phase for better event handling
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'auto';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      };
  }, [resizingColumn, resizeStartXRef, resizeStartWidthRef]);

  const handleResizeStart = (colIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't allow resizing S no column (handles all variations)
    if (isSnoColumnCheck(colIndex)) return;
    
    const column = e.currentTarget.closest('th');
    if (!column) return;
    
    // Get current width - prefer stored width, fallback to actual width
    const storedWidth = columnWidths[colIndex];
    const actualWidth = column.offsetWidth;
    const currentWidth = storedWidth || actualWidth;
    
    setResizingColumn(colIndex);
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = currentWidth;
  };

  // Helper function to check if a column is S no column (handles all variations)
  const isSnoColumnCheck = (colIndex, headerValue) => {
    if (colIndex !== 0) return false;
    const header = headerValue || displayData[0]?.row?.[colIndex];
    if (!header) return false;
    const headerLower = String(header).trim().toLowerCase();
    return headerLower === 's no' || headerLower === 'sno' || headerLower === 's no.';
  };

  const getColumnWidth = (colIndex) => {
    // S no column always has fixed width of 50px (handles "S no", "Sno", "s no", etc.)
    if (isSnoColumnCheck(colIndex)) {
      return 50; // Fixed width for S no column - consistent across all tables
    }
    // Return stored width or default width of 150px
    return columnWidths[colIndex] || 150;
  };

  const handleFillHandleMouseDown = (e, startRow, startCol) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFill(true);
    setFillStartCell({ row: startRow, col: startCol });
    setFillRange({ startRow, startCol, endRow: startRow, endCol: startCol });
    // Prevent cell click from firing
    setSelectedCell({ row: startRow, col: startCol });
    setIsSelecting(false);
  };

  const isInFillRange = (row, col) => {
    if (!fillRange) return false;
    return row >= fillRange.startRow && 
           row <= fillRange.endRow && 
           col >= fillRange.startCol && 
           col <= fillRange.endCol;
  };

  // Handle mouse up globally for selection
  useEffect(() => {
    const handleMouseUp = () => {
      // Restore text selection
      document.body.style.userSelect = '';
      
      setIsSelecting(false);
      
      if (isDraggingFill && fillRange && fillStartCell) {
        const newData = [...data];
        const sourceValue = data[fillStartCell.row][fillStartCell.col];
        
        // Fill all cells in the range with the source value
        saveToHistory(data);
        for (let row = fillRange.startRow; row <= fillRange.endRow; row++) {
          for (let col = fillRange.startCol; col <= fillRange.endCol; col++) {
            // Skip the source cell and header row
            if (row > 0 && !(row === fillStartCell.row && col === fillStartCell.col)) {
              newData[row][col] = sourceValue;
            }
          }
        }
        
        setData(newData);
        
        // Immediately save after fill handle
        if (tableId) {
          try {
            localStorage.setItem(`table_${tableId}`, JSON.stringify(newData));
            // Save immediately without debounce
            setTimeout(() => {
              saveData(newData, true);
            }, 100);
          } catch (error) {
            console.error('Error saving after fill handle:', error);
          }
        }
        
        setIsDraggingFill(false);
        setFillRange(null);
        setFillStartCell(null);
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingFill, fillRange, fillStartCell, data, isSelecting]);

  const handleCellMouseDown = async (row, col, e) => {
    if (e.button !== 0) return; // Only handle left mouse button
    
    // Check if click is inside a dropdown - if so, don't handle it here
    const target = e.target;
    if (target.closest && (target.closest('.currency-dropdown') || target.closest('.dropdown-option'))) {
      return; // Let dropdown handle the click
    }
    
    // Don't allow selection of header row or S no column
    if (row === 0 || isSnoColumnCheck(col, data[0]?.[col])) return;

    // If we're currently editing a cell, save it first before starting selection
    if (editingCell) {
      // Don't save if clicking the same cell
      if (editingCell.row !== row || editingCell.col !== col) {
        // Save the current edit before starting selection
        await handleInputBlur();
      }
    }

    // Prevent text selection during drag
    e.preventDefault();
    
    hasDraggedRef.current = false;
    setIsSelecting(true);
    
    // Handle Shift+Click: preserve anchor cell, extend selection
    if (e.shiftKey && selectedCell) {
      // Don't change the anchor (selectedCell), just update selection range
      // The handleCellClick will handle the full range calculation
      // For now, just mark that we're selecting
      setFillRange(null);
      setIsDraggingFill(false);
      setEditingCell(null);
      setShowDropdown(null);
    } else {
      // Normal click: set new anchor and clear selection range
    setSelectedCell({ row, col });
    setSelectionRange({ startRow: row, startCol: col, endRow: row, endCol: col });
    setFillRange(null);
    setIsDraggingFill(false);
    setEditingCell(null);
    setShowDropdown(null);
    }
    
    // Prevent text selection
    document.body.style.userSelect = 'none';
  };

  const handleCellMouseEnter = (row, col) => {
    // Handle fill handle drag (priority)
    if (isDraggingFill && fillStartCell) {
      const startRow = fillStartCell.row;
      const startCol = fillStartCell.col;
      
      const endRow = row;
      const endCol = col;
      
      setFillRange({
        startRow: Math.min(startRow, endRow),
        endRow: Math.max(startRow, endRow),
        startCol: Math.min(startCol, endCol),
        endCol: Math.max(startCol, endCol)
      });
      return;
    }
    
    // Handle drag selection
    if (isSelecting) {
      hasDraggedRef.current = true; // Mark that user is dragging
      if (row > 0 && !isSnoColumnCheck(col, data[0]?.[col])) {
        setSelectionRange(prev => {
          if (!prev) return { startRow: row, startCol: col, endRow: row, endCol: col };
          return {
            ...prev,
            endRow: row,
            endCol: col
          };
        });
        setSelectedCell({ row, col });
      }
    }
  };

  const handleCellMouseUp = () => {
    // Small delay to prevent click from firing after drag
    setTimeout(() => {
      setIsSelecting(false);
    }, 10);
  };

  const handleCellClick = async (row, col, e) => {
    // Check if click is inside a dropdown or dropdown option - if so, don't handle it here
    if (e && e.target && e.target.closest && (e.target.closest('.currency-dropdown') || e.target.closest('.dropdown-option'))) {
      return; // Let dropdown handle the click
    }
    
    if (row === 0 || isSnoColumnCheck(col, data[0]?.[col])) return;

    // Don't edit if we just finished dragging (user was selecting, not clicking)
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }

    // Handle Shift+Click for range selection (Shift-Select)
    if (e && e.shiftKey && selectedCell) {
      // Calculate the rectangular range from anchor (selectedCell) to target (clicked cell)
      const anchorRow = selectedCell.row;
      const anchorCol = selectedCell.col;
      
      // Calculate min/max to handle any direction (up, down, left, right, diagonal)
      const minRow = Math.min(anchorRow, row);
      const maxRow = Math.max(anchorRow, row);
      const minCol = Math.min(anchorCol, col);
      const maxCol = Math.max(anchorCol, col);
      
      // Set the selection range
      setSelectionRange({
        startRow: minRow,
        startCol: minCol,
        endRow: maxRow,
        endCol: maxCol
      });
      
      // Update selected cell to the clicked cell
      setSelectedCell({ row, col });
      
      // Don't open edit mode when shift-selecting
      setEditingCell(null);
      setShowDropdown(null);
      return;
    }

    // If we're currently editing a cell, save it first before opening a new one
    if (editingCell) {
      // Don't save if clicking the same cell
      if (editingCell.row === row && editingCell.col === col) {
        return; // Already editing this cell
      }
      // Save the current edit before opening a new one
      await handleInputBlur();
    }

    // Check if this is a custom dropdown column
    const isCustomDropdownCol = customDropdownColumns && customDropdownColumns[col];

    // Single click to edit (Excel-like behavior) or show dropdown for dropdown columns
    if (currencyColumns.includes(col) || isCustomDropdownCol) {
      // Currency or custom dropdown columns show dropdown
      // Toggle dropdown if clicking the same cell that already has dropdown open
      if (showDropdown?.row === row && showDropdown?.col === col) {
        setShowDropdown(null);
      } else {
        setShowDropdown({ row, col });
      }
      setEditingCell(null);
    } else {
      // Regular columns open edit mode
      setEditingCell({ row, col });
      // Parse value (remove commas, preserve spaces for Notes) when opening edit mode
      const cellValue = data[row]?.[col] || '';
      setEditValue(parseValueFromDisplay(cellValue, col));
      setShowDropdown(null);
    }
    
    // Clear selection range on normal click (not shift-click)
    if (!e || !e.shiftKey) {
      setSelectionRange(null);
    }
  };

  // Keep handleCellDoubleClick for backward compatibility but make it same as single click
  const handleCellDoubleClick = (row, col) => {
    handleCellClick(row, col);
  };

  const handleCurrencySelect = async (row, col, value) => {
    if (value === '+') {
      setShowAddCurrencyModal(true);
      setShowDropdown(null);
      return;
    }
    const newData = [...data];
    newData[row][col] = value;
    saveToHistory(data);
    setData(newData);
        // Immediately save currency change (also save to localStorage immediately)
        if (tableId) {
          try {
            // Save to localStorage immediately so stats update
            localStorage.setItem(`table_${tableId}`, JSON.stringify(newData));
            // Save immediately without debounce
            setTimeout(() => {
              saveData(newData, true);
            }, 100);
          } catch (error) {
            console.error('Error saving currency change:', error);
          }
        }
    setShowDropdown(null);
  };

  // Handle custom dropdown selection (for Source/Client type, etc.)
  const handleCustomDropdownSelect = async (row, col, value) => {
    const newData = [...data];
    const currentRow = Array.isArray(newData[row]) ? [...newData[row]] : [];
    currentRow[col] = value;
    newData[row] = currentRow;

    const headerRow = newData[0] || [];
    const headerValue = headerRow[col]
      ? String(headerRow[col]).toLowerCase()
      : '';

    if (value && headerValue === 'code') {
      const codeToNameMap = getCodeToNameMap();
      const lookup = String(value).trim();
      const foundName =
        codeToNameMap[lookup] ||
        codeToNameMap[lookup.toUpperCase()] ||
        codeToNameMap[lookup.toLowerCase()];
      if (foundName) {
        const nameIndex = headerRow.findIndex(
          (header) => header && header.toLowerCase() === 'name'
        );
        if (nameIndex !== -1) {
          currentRow[nameIndex] = foundName;
        }
      }
    } else if (value && headerValue === 'name') {
      const nameToCodeMap = getNameToCodeMap();
      const lookup = String(value).trim();
      const foundCode =
        nameToCodeMap[lookup] || nameToCodeMap[lookup.toLowerCase()];
      if (foundCode) {
        const codeIndex = headerRow.findIndex(
          (header) => header && header.toLowerCase() === 'code'
        );
        if (codeIndex !== -1) {
          currentRow[codeIndex] = foundCode;
        }
      }
    }

    saveToHistory(data);
    setData(newData);
    // Immediately save the change
    if (tableId) {
      try {
        localStorage.setItem(`table_${tableId}`, JSON.stringify(newData));
        setTimeout(() => {
          saveData(newData, true);
        }, 100);
      } catch (error) {
        console.error('Error saving custom dropdown change:', error);
      }
    }
    setShowDropdown(null);
  };

  const handleAddCurrency = () => {
    const trimmedCurrency = newCurrency.trim().toUpperCase();
    const allCurrencies = ['INR', 'AED', ...customCurrencies];
    if (trimmedCurrency && !allCurrencies.includes(trimmedCurrency)) {
      setCustomCurrencies([...customCurrencies, trimmedCurrency]);
      setNewCurrency('');
      setShowAddCurrencyModal(false);
    }
  };

  const handleRemoveCurrency = (currencyToRemove, e) => {
    e.stopPropagation();
    if (currencyToRemove !== 'INR' && currencyToRemove !== 'AED') {
      setCustomCurrencies(customCurrencies.filter(c => c !== currencyToRemove));
    }
  };

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleInputBlur = async () => {
    if (editingCell) {
      const newData = [...data];
      const rowIndex = editingCell.row;
      const colIndex = editingCell.col;
      const headerRow = newData[0];
      
      // Check if this is a Notes or Record column
      const currentHeader = headerRow[colIndex];
      const isNotesColumn = currentHeader && String(currentHeader).toLowerCase().trim() === 'notes';
      const isRecordColumn = currentHeader && String(currentHeader).toLowerCase().trim() === 'record';
      
      // Set the edited value (parse to remove commas, preserve spaces for Notes and Record)
      let parsedValue = parseValueFromDisplay(editValue, colIndex);
      
      // For Notes and Record columns, always store as string and prevent any date/number conversion
      if (isNotesColumn || isRecordColumn) {
        // Force to string and preserve exactly what user typed
        parsedValue = String(editValue || '');
      }
      
      newData[rowIndex][colIndex] = parsedValue;
      
      // Check if we're editing a Code column and if there's a Name column
      const isCodeColumn = currentHeader && (
        currentHeader.toLowerCase() === 'code' || 
        currentHeader.toLowerCase() === 'source'
      );
      
      if (isCodeColumn && editValue && editValue.trim()) {
        const codeToNameMap = getCodeToNameMap();
        const enteredCode = editValue.trim();
        
        // Look up the name for this code (case-insensitive)
        const lookupCode = enteredCode.toUpperCase();
        const foundName = codeToNameMap[enteredCode] || codeToNameMap[lookupCode] || codeToNameMap[enteredCode.toLowerCase()];
        
        if (foundName) {
          // Find the Name column index
          const nameIndex = headerRow.findIndex(
            header => header && header.toLowerCase() === 'name'
          );
          
          if (nameIndex !== -1 && nameIndex < newData[rowIndex].length) {
            // Auto-populate the Name column
            newData[rowIndex][nameIndex] = foundName;
          }
        }
      }
      
      const isNameColumn = currentHeader && currentHeader.toLowerCase() === 'name';
      if (isNameColumn && editValue && editValue.trim()) {
        const nameToCodeMap = getNameToCodeMap();
        const enteredName = editValue.trim();
        const foundCode =
          nameToCodeMap[enteredName] ||
          nameToCodeMap[enteredName.toLowerCase()];
        
        if (foundCode) {
          const codeIndex = headerRow.findIndex(
            header => header && header.toLowerCase() === 'code'
          );
          
          if (codeIndex !== -1 && codeIndex < newData[rowIndex].length) {
            newData[rowIndex][codeIndex] = foundCode;
          }
        }
      }
      
      // Auto-calculate Net Profit for A1 table (table1): Net Profit = Cross Profit - Expense
      if (tableId === 'table1') {
        const crossProfitIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'cross profit');
        const expenseIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'expense');
        const netProfitIndex = headerRow.findIndex(h => h && (h.toLowerCase() === 'net profit' || h.toLowerCase() === 'net-profit'));
        
        // If Cross Profit is edited, recalculate Net Profit
        if (colIndex === crossProfitIndex && crossProfitIndex !== -1 && expenseIndex !== -1 && netProfitIndex !== -1) {
          const crossProfitStr = String(newData[rowIndex][crossProfitIndex] || '').replace(/,/g, '');
          const expenseStr = String(newData[rowIndex][expenseIndex] || '').replace(/,/g, '');
          const crossProfit = parseFloat(crossProfitStr) || 0;
          const expense = parseFloat(expenseStr) || 0;
          const netProfit = crossProfit - expense;
          newData[rowIndex][netProfitIndex] = netProfit;
        }
        
        // If Expense is edited, recalculate Net Profit
        if (colIndex === expenseIndex && crossProfitIndex !== -1 && expenseIndex !== -1 && netProfitIndex !== -1) {
          const crossProfitStr = String(newData[rowIndex][crossProfitIndex] || '').replace(/,/g, '');
          const expenseStr = String(newData[rowIndex][expenseIndex] || '').replace(/,/g, '');
          const crossProfit = parseFloat(crossProfitStr) || 0;
          const expense = parseFloat(expenseStr) || 0;
          const netProfit = crossProfit - expense;
          newData[rowIndex][netProfitIndex] = netProfit;
        }
      }
      
      // Auto-calculate Cross Profit and Net Profit for Transactions table (table4)
      if (tableId === 'table4') {
        const aedIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'aed');
        const sellingRateIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'selling rate');
        const stockInrIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'stock inr');
        const inrIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'inr');
        const crossProfitIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'cross profit');
        const expenseIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'expense');
        const netProfitIndex = headerRow.findIndex(h => h && h.toLowerCase() === 'net profit');
        
        // Auto-calculate formulas for Transactions table (table4)
        // Priority: If AED is edited -> calculate Selling Rate
        //           If INR is edited -> calculate Selling Rate (if AED exists) OR calculate AED (if Selling Rate exists)
        //           If Selling Rate is edited -> calculate AED
        
        if (inrIndex !== -1 && aedIndex !== -1 && sellingRateIndex !== -1) {
          const inrStr = String(newData[rowIndex][inrIndex] || '').replace(/,/g, '');
          const aedStr = String(newData[rowIndex][aedIndex] || '').replace(/,/g, '');
          const sellingRateStr = String(newData[rowIndex][sellingRateIndex] || '').replace(/,/g, '');
          const inr = parseFloat(inrStr) || 0;
          const aed = parseFloat(aedStr) || 0;
          const sellingRate = parseFloat(sellingRateStr) || 0;
          
          // If AED column is edited, calculate Selling Rate = INR / AED
          if (colIndex === aedIndex && aed !== 0 && inr !== 0) {
            const calculatedSellingRate = inr / aed;
            // Round Selling Rate to 2 decimal places
            const roundedSellingRate = Math.round(calculatedSellingRate * 100) / 100;
            newData[rowIndex][sellingRateIndex] = roundedSellingRate;
          }
          // If INR column is edited
          else if (colIndex === inrIndex) {
            // Priority: If AED exists, calculate Selling Rate
            if (aed !== 0 && inr !== 0) {
              const calculatedSellingRate = inr / aed;
              // Round Selling Rate to 2 decimal places
              const roundedSellingRate = Math.round(calculatedSellingRate * 100) / 100;
              newData[rowIndex][sellingRateIndex] = roundedSellingRate;
            }
            // If AED doesn't exist but Selling Rate exists, calculate AED
            else if (sellingRate !== 0 && inr !== 0) {
              const calculatedAed = inr / sellingRate;
              // Round AED to nearest whole number (no decimals)
              const roundedAed = Math.round(calculatedAed);
              newData[rowIndex][aedIndex] = roundedAed;
            }
          }
          // If Selling Rate column is edited, calculate AED = INR / Selling Rate
          else if (colIndex === sellingRateIndex && sellingRate !== 0 && inr !== 0) {
            const calculatedAed = inr / sellingRate;
            // Round AED to nearest whole number (no decimals)
            const roundedAed = Math.round(calculatedAed);
            newData[rowIndex][aedIndex] = roundedAed;
          }
          
          // Clear values if inputs become invalid
          if (colIndex === aedIndex && aed === 0 && inr !== 0) {
            newData[rowIndex][sellingRateIndex] = '';
          }
          if (colIndex === sellingRateIndex && sellingRate === 0 && inr !== 0) {
            newData[rowIndex][aedIndex] = '';
          }
        }
        
        // If Stock INR or INR is edited, recalculate Cross Profit
        if ((colIndex === stockInrIndex || colIndex === inrIndex) && stockInrIndex !== -1 && inrIndex !== -1 && crossProfitIndex !== -1) {
          const stockInrStr = String(newData[rowIndex][stockInrIndex] || '').replace(/,/g, '');
          const inrStr = String(newData[rowIndex][inrIndex] || '').replace(/,/g, '');
          const stockInr = parseFloat(stockInrStr) || 0;
          const inr = parseFloat(inrStr) || 0;
          const crossProfit = stockInr - inr;
          newData[rowIndex][crossProfitIndex] = crossProfit;
          
          // Also recalculate Net Profit if Cross Profit changed
          if (expenseIndex !== -1 && netProfitIndex !== -1) {
            const expenseStr = String(newData[rowIndex][expenseIndex] || '').replace(/,/g, '');
            const expense = parseFloat(expenseStr) || 0;
            const netProfit = crossProfit - expense;
            newData[rowIndex][netProfitIndex] = netProfit;
          }
        }
        
        // If Cross Profit is edited, recalculate Net Profit
        if (colIndex === crossProfitIndex && crossProfitIndex !== -1 && expenseIndex !== -1 && netProfitIndex !== -1) {
          const crossProfitStr = String(newData[rowIndex][crossProfitIndex] || '').replace(/,/g, '');
          const expenseStr = String(newData[rowIndex][expenseIndex] || '').replace(/,/g, '');
          const crossProfit = parseFloat(crossProfitStr) || 0;
          const expense = parseFloat(expenseStr) || 0;
          const netProfit = crossProfit - expense;
          newData[rowIndex][netProfitIndex] = netProfit;
        }
        
        // If Expense is edited, recalculate Net Profit
        if (colIndex === expenseIndex && crossProfitIndex !== -1 && expenseIndex !== -1 && netProfitIndex !== -1) {
          const crossProfitStr = String(newData[rowIndex][crossProfitIndex] || '').replace(/,/g, '');
          const expenseStr = String(newData[rowIndex][expenseIndex] || '').replace(/,/g, '');
          const crossProfit = parseFloat(crossProfitStr) || 0;
          const expense = parseFloat(expenseStr) || 0;
          const netProfit = crossProfit - expense;
          newData[rowIndex][netProfitIndex] = netProfit;
        }
      }
      
      if (tableId === 'table7' || tableId === 'table7_moneyin' || tableId === 'table7_moneyout') {
        recalcLoanRowValues(newData[rowIndex], headerRow);
      }
      
      saveToHistory(data);
      setData(newData);
      
      // Immediately save the data after cell edit - COMPLETELY RECODED
      if (tableId) {
        try {
          // Save immediately to MySQL (no delay, no debounce)
          await saveData(newData, true);
        } catch (error) {
          console.error('❌ [CELL EDIT] Error saving after cell edit:', error);
        }
      }
      
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentRow = editingCell.row;
      const currentCol = editingCell.col;
      handleInputBlur();
      
      // Move to next row (Excel behavior)
      setTimeout(() => {
        if (currentRow < data.length - 1) {
          const nextRow = currentRow + 1;
          setSelectedCell({ row: nextRow, col: currentCol });
          setSelectionRange(null);
          // Start editing the next cell
          const isCustomDropdownCol = customDropdownColumns && customDropdownColumns[currentCol];
          if (!currencyColumns.includes(currentCol) && !isCustomDropdownCol) {
            setEditingCell({ row: nextRow, col: currentCol });
            // Parse value (remove commas, preserve spaces for Notes) when opening edit mode
            const cellValue = data[nextRow]?.[currentCol] || '';
            setEditValue(parseValueFromDisplay(cellValue, currentCol));
          }
        }
      }, 50);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const currentRow = editingCell.row;
      const currentCol = editingCell.col;
      handleInputBlur();
      
      // Move to next column (or next row if at end)
      setTimeout(() => {
        const maxCol = (data[0]?.length || 1) - 1;
          if (currentCol < maxCol) {
            setSelectedCell({ row: currentRow, col: currentCol + 1 });
            setSelectionRange(null);
            const isCustomDropdownCol = customDropdownColumns && customDropdownColumns[currentCol + 1];
            if (!currencyColumns.includes(currentCol + 1) && !isCustomDropdownCol) {
              setEditingCell({ row: currentRow, col: currentCol + 1 });
              // Parse value (remove commas) when opening edit mode
              const cellValue = data[currentRow]?.[currentCol + 1] || '';
              setEditValue(parseValueFromDisplay(cellValue, currentCol + 1));
            }
        } else if (currentRow < data.length - 1) {
          // Move to next row, first column
          const nextRow = currentRow + 1;
          setSelectedCell({ row: nextRow, col: 0 });
          setSelectionRange(null);
        }
      }, 50);
    }
  };

  const handleHeaderInputBlur = async () => {
    if (editingHeader !== null) {
      const newData = [...data];
      newData[0][editingHeader] = headerEditValue;
      saveToHistory(data);
      setData(newData);
      
      // Immediately save the data after header edit
      if (tableId) {
        try {
          localStorage.setItem(`table_${tableId}`, JSON.stringify(newData));
          // Save immediately without debounce
          setTimeout(() => {
            saveData(newData, true);
          }, 100);
        } catch (error) {
          console.error('Error saving after header edit:', error);
        }
      }
      
      setEditingHeader(null);
      setHeaderEditValue('');
    }
  };

  const handleHeaderInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleHeaderInputBlur();
    } else if (e.key === 'Escape') {
      setEditingHeader(null);
      setHeaderEditValue('');
    }
  };

  const addRow = async (count = 1) => {
    saveToHistory(data);
    const newRows = Array(count).fill(null).map(() => Array(cols).fill(''));
    const newData = [...data, ...newRows];
    setData(newData);
    // Immediately save the data - COMPLETELY RECODED
    if (tableId) {
      try {
        await saveData(newData, true);
      } catch (error) {
        console.error('❌ [ADD ROW] Error saving after add row:', error);
      }
    }
  };

  const handleAddMultipleRows = () => {
    const count = parseInt(rowsToAdd, 10);
    if (count > 0 && count <= 1000) {
      addRow(count);
      setShowAddRowsModal(false);
      setRowsToAdd('1');
    }
  };

  const scrollToTop = () => {
    if (tableWrapperRef.current && data.length > 1) {
      // Find first filled row in displayed data (skip header row 0)
      const sortedData = getSortedData();
      const filteredData = getFilteredData(sortedData);
      const headerRow = filteredData[0]?.row || [];
      
      // Find first row with data (skip header)
      let firstFilledRowIndex = null;
      for (let i = 1; i < filteredData.length; i++) {
        const row = filteredData[i].row;
        if (row && row.some((cell, colIndex) => {
          // Skip S no column (always has value, so exclude it)
          if (isSnoColumnCheck(colIndex, headerRow[colIndex])) return false;
          if (cell === null || cell === undefined) return false;
          if (typeof cell === 'string') return cell.trim() !== '';
          return cell !== '';
        })) {
          firstFilledRowIndex = filteredData[i].originalIndex;
          break;
        }
      }
      
      if (firstFilledRowIndex !== null) {
        const firstRowElement = tableWrapperRef.current.querySelector(`tbody tr[data-row="${firstFilledRowIndex}"]`);
        if (firstRowElement) {
          firstRowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
      tableWrapperRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        tableWrapperRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const scrollToLastDate = () => {
    if (!tableWrapperRef.current || data.length <= 1) return;
    
    const headerRow = data[0] || [];
    const tbody = tableWrapperRef.current.querySelector('tbody');
    if (!tbody) return;
    
    // Find Date column index
    const dateColumnIndex = headerRow.findIndex(h => {
      const header = String(h || '').toLowerCase().trim();
      return header === 'date';
    });
    
    if (dateColumnIndex === -1) {
      console.log('No date column found');
      return;
    }
    
    // Helper function to check if a date value is valid
    const hasValidDate = (dateValue) => {
      if (dateValue === null || dateValue === undefined) return false;
      const dateStr = String(dateValue).trim();
      if (dateStr === '') return false;
      // Check if it looks like a date (has / or - separator)
      return dateStr.includes('/') || dateStr.includes('-');
    };
    
    // Find the last row with a valid date (iterate backwards from end)
    let lastDateRowIndex = null;
    for (let i = data.length - 1; i >= 1; i--) {
      const row = data[i];
      if (!row || !Array.isArray(row)) continue;
      
      // Check if row has any data (excluding S no)
      let hasData = false;
      for (let colIdx = 0; colIdx < row.length; colIdx++) {
        if (isSnoColumnCheck(colIdx, headerRow[colIdx])) continue;
        const cell = row[colIdx];
        if (cell !== null && cell !== undefined) {
          if (typeof cell === 'string' && cell.trim() !== '') {
            hasData = true;
            break;
          } else if (typeof cell !== 'string' && cell !== '') {
            hasData = true;
            break;
          }
        }
      }
      
      if (!hasData) continue; // Skip empty rows
      
      // Check if this row has a valid date
      const dateValue = row[dateColumnIndex];
      if (hasValidDate(dateValue)) {
        lastDateRowIndex = i;
        break;
      }
    }
    
    if (lastDateRowIndex === null) {
      console.log('No date found in table');
      return;
    }
    
    console.log('Found last date row:', lastDateRowIndex);
    
    // Find the row element in DOM
    const targetRow = tbody.querySelector(`tr[data-row="${lastDateRowIndex}"]`);
    
    // Find row by looking for a cell with the data-row attribute
    const targetCell = tbody.querySelector(`td[data-row="${lastDateRowIndex}"]`);
    
    if (targetCell) {
      console.log('Scrolling to row:', lastDateRowIndex);
      const targetRow = targetCell.closest('tr');
      if (targetRow) {
        targetRow.scrollIntoView({ behavior: 'smooth', block: 'end' });
        return;
      }
    }
    
    console.log('Row not in DOM, checking visible rows');
    // Row might be filtered, find last visible row with date
    // Get all cells with data-row attributes, then get their parent rows
    const allCells = Array.from(tbody.querySelectorAll('td[data-row]'));
    
    if (allCells.length === 0) {
      console.log('No visible cells found');
      return;
    }
    
    // Group cells by row index
    const rowsByIndex = {};
    allCells.forEach(cell => {
      const rowIdx = parseInt(cell.getAttribute('data-row'));
      if (!isNaN(rowIdx) && rowIdx >= 1 && rowIdx < data.length) {
        if (!rowsByIndex[rowIdx]) {
          rowsByIndex[rowIdx] = cell.closest('tr');
        }
      }
    });
    
    // Find the last visible row that has a date
    const sortedIndices = Object.keys(rowsByIndex).map(Number).sort((a, b) => b - a);
    
    for (const rowIdx of sortedIndices) {
      const rowEl = rowsByIndex[rowIdx];
      if (!rowEl) continue;
      
      const rowData = data[rowIdx];
      if (!rowData || !Array.isArray(rowData)) continue;
      
      // Check if row has any data (excluding S no)
      let hasData = false;
      for (let colIdx = 0; colIdx < rowData.length; colIdx++) {
        if (isSnoColumnCheck(colIdx, headerRow[colIdx])) continue;
        const cell = rowData[colIdx];
        if (cell !== null && cell !== undefined) {
          if (typeof cell === 'string' && cell.trim() !== '') {
            hasData = true;
            break;
          } else if (typeof cell !== 'string' && cell !== '') {
            hasData = true;
            break;
          }
        }
      }
      
      if (!hasData) continue;
      
      const dateValue = rowData[dateColumnIndex];
      if (hasValidDate(dateValue)) {
        console.log('Found date in visible row:', rowIdx);
        rowEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
        return;
      }
    }
    
    console.log('No visible date row found, scrolling to last visible row');
    // Fallback: scroll to the last visible row
    if (sortedIndices.length > 0) {
      const lastRowIdx = sortedIndices[0];
      const lastRow = rowsByIndex[lastRowIdx];
      if (lastRow) {
        lastRow.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  };

  const addColumn = async () => {
    // Find the next available column number for generic naming
    const existingHeaders = data[0] || [];
    let columnNumber = 1;
    const usedNumbers = new Set();
    
    // Extract numbers from existing "Column X" headers
    existingHeaders.forEach(header => {
      if (header && typeof header === 'string' && header.startsWith('Column ')) {
        const match = header.match(/Column (\d+)/);
        if (match) {
          usedNumbers.add(parseInt(match[1], 10));
        }
      }
    });
    
    // Find the next available number
    while (usedNumbers.has(columnNumber)) {
      columnNumber++;
    }
    
    const newHeaderName = `Column ${columnNumber}`;
    saveToHistory(data);
    const newData = data.map((row, index) => {
      if (index === 0) {
        // Header row gets the new generic column name
        return [...row, newHeaderName];
      } else {
        // Data rows get empty string
        return [...row, ''];
      }
    });
    setData(newData);
    // Immediately save the data
    if (tableId) {
      try {
        localStorage.setItem(`table_${tableId}`, JSON.stringify(newData));
        // Save immediately without debounce
        setTimeout(() => {
          saveData(newData, true);
        }, 100);
      } catch (error) {
        console.error('Error saving after add column:', error);
      }
    }
  };

  const deleteRow = (rowIndex) => {
    // Don't allow deleting header row (index 0)
    if (rowIndex === 0) {
      return;
    }
    // Allow deleting any data row
    saveToHistory(data);
    const newData = data.filter((_, index) => index !== rowIndex);
    setData(newData);
    // Immediately save the data
    if (tableId) {
      try {
        localStorage.setItem(`table_${tableId}`, JSON.stringify(newData));
        // Save immediately without debounce
        setTimeout(() => {
          saveData(newData, true);
        }, 100);
      } catch (error) {
        console.error('Error saving after delete row:', error);
      }
    }
  };

  const deleteColumn = (colIndex) => {
    // Only allow deleting columns that were added (not original)
    if (isColumnDeletable(colIndex)) {
      saveToHistory(data);
      const newData = data.map(row => row.filter((_, index) => index !== colIndex));
      setData(newData);
      // Immediately save the data
      if (tableId) {
        try {
          localStorage.setItem(`table_${tableId}`, JSON.stringify(newData));
          // Save immediately without debounce
          setTimeout(() => {
            saveData(newData, true);
          }, 100);
        } catch (error) {
          console.error('Error saving after delete column:', error);
        }
      }
    }
  };

  const getCellId = (row, col) => {
    return `${String.fromCharCode(65 + col)}${row + 1}`;
  };

  // Calculate totals for export (with currency support)
  const calculateTotalsForExport = () => {
    if (!data || data.length < 2) return null;
    
    const headerRow = data[0];
    const totalsRow = [];
    
    // Determine which columns should have totals/averages
    const numericHeaders = ['amount', 'cross profit', 'expense', 'net profit', 'net-profit', 'total amount', 'buying rate', 'rate'];
    
    // For tables with currency columns, calculate totals by currency
    if (usesCurrencyTotals && currencyColumns.length > 0) {
      const currencyColIndex = currencyColumns[0];
      const totalsByCurrency = {};
      
      // Calculate totals by currency
      for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
        const currency = String(data[rowIndex]?.[currencyColIndex] || '').trim().toUpperCase();
        if (!currency) continue;
        
        if (!totalsByCurrency[currency]) {
          totalsByCurrency[currency] = {};
        }
        
    headerRow.forEach((header, colIndex) => {
      const headerStr = String(header || '').trim().toLowerCase();
          const shouldCalculateTotal = totalColumns.includes(colIndex) || numericHeaders.includes(headerStr);
          
          if (shouldCalculateTotal) {
            const cellValue = data[rowIndex]?.[colIndex];
            if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
              const numValue = parseFloat(String(cellValue).replace(/[^0-9.-]/g, ''));
              if (!isNaN(numValue)) {
                totalsByCurrency[currency][colIndex] = (totalsByCurrency[currency][colIndex] || 0) + numValue;
              }
            }
          }
        });
      }
      
      // Build totals row showing both AED and INR
      headerRow.forEach((header, colIndex) => {
        const headerStr = String(header || '').trim().toLowerCase();
        
      if (headerStr === 's no' || headerStr === 'sno' || headerStr === 's no.' || colIndex === 0) {
        totalsRow.push('');
        return;
      }
      
        const shouldCalculateTotal = totalColumns.includes(colIndex) || numericHeaders.includes(headerStr);
        const shouldCalculateAverage = columnAverages[colIndex];
        
        if (!shouldCalculateTotal && !shouldCalculateAverage) {
          totalsRow.push('');
          return;
        }
        
        // Show both AED and INR totals if available
        const inrTotal = totalsByCurrency['INR']?.[colIndex] || 0;
        const aedTotal = totalsByCurrency['AED']?.[colIndex] || 0;
        
        if (shouldCalculateTotal && (inrTotal !== 0 || aedTotal !== 0)) {
          const parts = [];
          if (inrTotal !== 0) parts.push(`INR: ₹${inrTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
          if (aedTotal !== 0) parts.push(`AED: ${aedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
          totalsRow.push(parts.join(' | ') || '');
        } else if (shouldCalculateAverage) {
          // For averages, calculate normally
          let total = 0;
          let count = 0;
          for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
            const cellValue = data[rowIndex]?.[colIndex];
            if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
              const numValue = parseFloat(String(cellValue).replace(/[^0-9.-]/g, ''));
              if (!isNaN(numValue)) {
                total += numValue;
                count++;
              }
            }
          }
          const avg = count > 0 ? total / count : 0;
          totalsRow.push(avg.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        } else {
          totalsRow.push('');
        }
      });
    } else {
      // For other tables, calculate normally
      headerRow.forEach((header, colIndex) => {
        const headerStr = String(header || '').trim().toLowerCase();
        
        if (headerStr === 's no' || headerStr === 'sno' || headerStr === 's no.' || colIndex === 0) {
          totalsRow.push('');
          return;
        }
        
      const shouldCalculateTotal = totalColumns.includes(colIndex) || numericHeaders.includes(headerStr);
      const shouldCalculateAverage = columnAverages[colIndex];
      
      if (!shouldCalculateTotal && !shouldCalculateAverage) {
        totalsRow.push('');
        return;
      }
      
      let total = 0;
      let count = 0;
      let hasNumericValues = false;
      
      for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
        const cellValue = data[rowIndex]?.[colIndex];
        if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
          const numValue = parseFloat(String(cellValue).replace(/[^0-9.-]/g, ''));
          if (!isNaN(numValue)) {
            total += numValue;
            count++;
            hasNumericValues = true;
          }
        }
      }
      
      if (hasNumericValues) {
        if (shouldCalculateAverage) {
          const avg = count > 0 ? total / count : 0;
          totalsRow.push(avg.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        } else if (shouldCalculateTotal) {
          totalsRow.push(total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        } else {
          totalsRow.push('');
        }
      } else {
        totalsRow.push('');
      }
    });
    }
    
    return totalsRow;
  };

  // Get unique values from data for filter dropdowns
  const getUniqueValues = (columnName) => {
    if (!data || data.length < 2) return [];
    const headerRow = data[0];
    const colIndex = headerRow.findIndex(h => h && h.toLowerCase() === columnName.toLowerCase());
    if (colIndex === -1) return [];
    
    const values = new Set();
    data.slice(1).forEach(row => {
      const value = String(row[colIndex] || '').trim();
      if (value) values.add(value);
    });
    return Array.from(values).sort();
  };


  // Export to CSV with filters
  const exportToCSV = () => {
    try {
      if (!displayData || displayData.length === 0) {
        alert('No data to export');
        return;
      }

      const rows = [];
      
      // Add header row
      const headerRow = displayData[0]?.row || [];
      rows.push(headerRow.map(cell => {
        const cellValue = cell !== null && cell !== undefined ? String(cell) : '';
        if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
          return `"${cellValue.replace(/"/g, '""')}"`;
        }
        return cellValue;
      }).join(','));

      // Add data rows
      displayData.slice(1).forEach(item => {
        const row = item.row || [];
        rows.push(row.map(cell => {
          const cellValue = cell !== null && cell !== undefined ? String(cell) : '';
          if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
            return `"${cellValue.replace(/"/g, '""')}"`;
          }
          return cellValue;
        }).join(','));
      });

      // Add totals row if available
      const totalsRow = calculateTotalsForExport();
      if (totalsRow && totalsRow.some(cell => cell !== '')) {
        rows.push('');
        rows.push(['TOTAL', ...totalsRow.slice(1)].map(cell => {
          const cellValue = cell !== null && cell !== undefined ? String(cell) : '';
          if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
            return `"${cellValue.replace(/"/g, '""')}"`;
          }
          return cellValue;
        }).join(','));
      }

      const csvContent = rows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${tableName || 'table'}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Error exporting to CSV. Please try again.');
    }
  };

  // Export to PDF
  const exportToPDF = (exportType = 'all') => {
    try {
      if (!displayData || displayData.length === 0) {
        alert('No data to export');
        return;
      }

      // Filter data based on export type for table5 (Source and Client)
      let filteredDisplayData = displayData;
      let exportTitle = tableName || 'Table';
      
      if (tableId === 'table5' && exportType !== 'all') {
        const headerRow = displayData[0]?.row || [];
        const typeIndex = headerRow.findIndex(h => String(h).toLowerCase() === 'type');
        
        if (typeIndex !== -1) {
          const header = displayData[0];
          const filteredRows = displayData.slice(1).filter(item => {
            const row = item.row || [];
            const type = String(row[typeIndex] || '').trim().toLowerCase();
            if (exportType === 'source') {
              // Include both 'source' and 'hybrid' in source exports
              return type === 'source' || type === 'hybrid';
            } else if (exportType === 'client') {
              // Include both 'client' and 'hybrid' in client exports
              return type === 'client' || type === 'hybrid';
            }
            return true;
          });
          
          filteredDisplayData = [header, ...filteredRows];
          
          // Update title based on export type
          if (exportType === 'source') {
            exportTitle = `${tableName || 'Table'} - Sources`;
          } else if (exportType === 'client') {
            exportTitle = `${tableName || 'Table'} - Clients`;
          }
        }
      }

      if (filteredDisplayData.length <= 1) {
        alert(`No ${exportType === 'source' ? 'Sources' : exportType === 'client' ? 'Clients' : 'data'} to export`);
        return;
      }

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Add title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(exportTitle, 14, 15);

      // Add export date
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const exportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      doc.text(`Exported on: ${exportDate}`, 14, 22);

      // Prepare table data
      const headerRow = filteredDisplayData[0]?.row || [];
      
      // Check if first column is S No column
      const isFirstColumnSNo = headerRow[0] && (
        String(headerRow[0]).toLowerCase().trim() === 's no' ||
        String(headerRow[0]).toLowerCase().trim() === 'sno' ||
        String(headerRow[0]).toLowerCase().trim() === 's no.'
      );
      
      // Calculate stats for tables with currency columns (like table1)
      let stats = null;
      if (tableId === 'table1' && currencyColumns.length > 0) {
        const currencyColIndex = currencyColumns[0];
        const amountIndex = headerRow.findIndex(h => String(h).toLowerCase().trim() === 'amount');
        const crossProfitIndex = headerRow.findIndex(h => String(h).toLowerCase().trim() === 'cross profit');
        const expenseIndex = headerRow.findIndex(h => String(h).toLowerCase().trim() === 'expense');
        const netProfitIndex = headerRow.findIndex(h => String(h).toLowerCase().trim() === 'net-profit' || String(h).toLowerCase().trim() === 'net profit');
        
        if (amountIndex !== -1 || crossProfitIndex !== -1 || expenseIndex !== -1 || netProfitIndex !== -1) {
          let amount = 0, crossProfit = 0, expense = 0, netProfit = 0;
          const currencyBreakdown = {};
          
          filteredDisplayData.slice(1).forEach((item, rowIndex) => {
            const row = item.row || [];
            const rowCurrency = currencyColIndex !== -1 && row[currencyColIndex] 
              ? String(row[currencyColIndex]).toUpperCase().trim() 
              : '';
            
            // Always add to main totals
            if (amountIndex !== -1 && row[amountIndex]) {
              const value = parseFloat(String(row[amountIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
              amount += value;
            }
            if (crossProfitIndex !== -1 && row[crossProfitIndex]) {
              const value = parseFloat(String(row[crossProfitIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
              crossProfit += value;
            }
            if (expenseIndex !== -1 && row[expenseIndex]) {
              const value = parseFloat(String(row[expenseIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
              expense += value;
            }
            if (netProfitIndex !== -1 && row[netProfitIndex]) {
              const value = parseFloat(String(row[netProfitIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
              netProfit += value;
            }
            
            // Track non-INR currencies
            if (rowCurrency && rowCurrency !== 'INR' && rowCurrency !== '') {
              if (!currencyBreakdown[rowCurrency]) {
                currencyBreakdown[rowCurrency] = { amount: 0, crossProfit: 0, expense: 0, netProfit: 0 };
              }
              
              if (amountIndex !== -1 && row[amountIndex]) {
                const value = parseFloat(String(row[amountIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
                currencyBreakdown[rowCurrency].amount += value;
              }
              if (crossProfitIndex !== -1 && row[crossProfitIndex]) {
                const value = parseFloat(String(row[crossProfitIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
                currencyBreakdown[rowCurrency].crossProfit += value;
              }
              if (expenseIndex !== -1 && row[expenseIndex]) {
                const value = parseFloat(String(row[expenseIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
                currencyBreakdown[rowCurrency].expense += value;
              }
              if (netProfitIndex !== -1 && row[netProfitIndex]) {
                const value = parseFloat(String(row[netProfitIndex]).replace(/[^0-9.-]/g, '') || '0') || 0;
                currencyBreakdown[rowCurrency].netProfit += value;
              }
            }
          });
          
          // Calculate INR totals (main totals minus non-INR currencies)
          const nonInrAmount = Object.values(currencyBreakdown).reduce((sum, val) => sum + (val.amount || 0), 0);
          const nonInrCrossProfit = Object.values(currencyBreakdown).reduce((sum, val) => sum + (val.crossProfit || 0), 0);
          const nonInrExpense = Object.values(currencyBreakdown).reduce((sum, val) => sum + (val.expense || 0), 0);
          const nonInrNetProfit = Object.values(currencyBreakdown).reduce((sum, val) => sum + (val.netProfit || 0), 0);
          
          stats = {
            inr: {
              amount: amount - nonInrAmount,
              crossProfit: crossProfit - nonInrCrossProfit,
              expense: expense - nonInrExpense,
              netProfit: netProfit - nonInrNetProfit
            },
            currencyBreakdown
          };
        }
      }
      
      const dataRows = filteredDisplayData.slice(1).map((item, rowIndex) => {
        const row = item.row || [];
        return row.map((cell, colIndex) => {
          // If this is the S No column (first column), add row number
          if (isFirstColumnSNo && colIndex === 0) {
            return String(rowIndex + 1);
          }
          if (cell === null || cell === undefined) return '';
          return String(cell);
        });
      });

      // Calculate totals for export
      const totalsRow = calculateTotalsForExport();
      const footRows = [];
      
      // Add totals row if available
      if (totalsRow && totalsRow.some(cell => cell !== '')) {
        footRows.push(['TOTAL', ...totalsRow.slice(1)]);
      }

      // Add stats section before table if available
      let startY = 28;
      if (stats) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Statistics', 14, startY);
        startY += 6;
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        // Amount
        if (stats.inr.amount !== 0 || stats.currencyBreakdown?.AED?.amount) {
          doc.setFont(undefined, 'bold');
          doc.text('Amount:', 14, startY);
          doc.setFont(undefined, 'normal');
          let xPos = 35;
          if (stats.inr.amount !== 0) {
            doc.text(`INR: ₹${stats.inr.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
            xPos += 60;
          }
          if (stats.currencyBreakdown?.AED?.amount) {
            doc.text(`AED: AED ${stats.currencyBreakdown.AED.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
          }
          startY += 5;
        }
        
        // Cross Profit
        if (stats.inr.crossProfit !== 0 || stats.currencyBreakdown?.AED?.crossProfit) {
          doc.setFont(undefined, 'bold');
          doc.text('Cross Profit:', 14, startY);
          doc.setFont(undefined, 'normal');
          let xPos = 35;
          if (stats.inr.crossProfit !== 0) {
            doc.text(`INR: ₹${stats.inr.crossProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
            xPos += 60;
          }
          if (stats.currencyBreakdown?.AED?.crossProfit) {
            doc.text(`AED: AED ${stats.currencyBreakdown.AED.crossProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
          }
          startY += 5;
        }
        
        // Expense
        if (stats.inr.expense !== 0 || stats.currencyBreakdown?.AED?.expense) {
          doc.setFont(undefined, 'bold');
          doc.text('Expense:', 14, startY);
          doc.setFont(undefined, 'normal');
          let xPos = 35;
          if (stats.inr.expense !== 0) {
            doc.text(`INR: ₹${stats.inr.expense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
            xPos += 60;
          }
          if (stats.currencyBreakdown?.AED?.expense) {
            doc.text(`AED: AED ${stats.currencyBreakdown.AED.expense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
          }
          startY += 5;
        }
        
        // Net Profit
        if (stats.inr.netProfit !== 0 || stats.currencyBreakdown?.AED?.netProfit) {
          doc.setFont(undefined, 'bold');
          doc.text('Net Profit:', 14, startY);
          doc.setFont(undefined, 'normal');
          let xPos = 35;
          if (stats.inr.netProfit !== 0) {
            doc.text(`INR: ₹${stats.inr.netProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
            xPos += 60;
          }
          if (stats.currencyBreakdown?.AED?.netProfit) {
            doc.text(`AED: AED ${stats.currencyBreakdown.AED.netProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, xPos, startY);
          }
          startY += 5;
        }
        
        startY += 3; // Add some space before table
      }

      // Create table using autoTable function
      autoTable(doc, {
        head: [headerRow.map(h => String(h || ''))],
        body: dataRows,
        foot: footRows.length > 0 ? footRows : undefined,
        startY: startY,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: {
          fillColor: [249, 250, 251],
          textColor: [17, 24, 39],
          fontStyle: 'bold',
          halign: 'left'
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        footStyles: {
          fillColor: [243, 244, 246],
          textColor: [17, 24, 39],
          fontStyle: 'bold'
        },
        margin: { top: 28, left: 14, right: 14 },
        didDrawPage: function (data) {
          // Add page numbers
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber}`,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
          );
        }
      });

      // Save PDF
      const filenameSuffix = exportType === 'source' ? '_Sources' : exportType === 'client' ? '_Clients' : '';
      doc.save(`${exportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Error exporting to PDF. Please try again.');
    }
  };

  return (
    <div className="excel-table-container">
      <div className="excel-toolbar">
        <button onClick={() => addRow(1)} className="toolbar-button">+ Add Row</button>
        <button onClick={() => setShowAddRowsModal(true)} className="toolbar-button">+ Add Multiple Rows</button>
        <button onClick={addColumn} className="toolbar-button">+ Add Column</button>
        <div className="toolbar-separator"></div>
        <button onClick={exportToCSV} className="toolbar-button export-button" title="Export to CSV">
          📥 Export CSV
        </button>
        {tableId === 'table5' ? (
          <div className="export-pdf-dropdown" ref={exportMenuRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button 
              onClick={() => setShowExportMenu(!showExportMenu)} 
              className="toolbar-button export-button" 
              title="Export to PDF"
            >
          📄 Export PDF
        </button>
            {showExportMenu && (
              <div className="export-menu">
                <button 
                  onClick={() => { exportToPDF('all'); setShowExportMenu(false); }} 
                  className="export-menu-item"
                >
                  Export All
                </button>
                <button 
                  onClick={() => { exportToPDF('source'); setShowExportMenu(false); }} 
                  className="export-menu-item"
                >
                  Export Sources
                </button>
                <button 
                  onClick={() => { exportToPDF('client'); setShowExportMenu(false); }} 
                  className="export-menu-item"
                >
                  Export Clients
                </button>
              </div>
            )}
          </div>
        ) : (
          <button onClick={() => exportToPDF('all')} className="toolbar-button export-button" title="Export to PDF">
            📄 Export PDF
          </button>
        )}
        <button onClick={undo} disabled={!canUndo} className="toolbar-button" title="Undo (Ctrl+Z / Cmd+Z)">
          ↶ Undo
        </button>
        <button onClick={redo} disabled={!canRedo} className="toolbar-button" title="Redo (Ctrl+Y / Cmd+Shift+Z)">
          ↷ Redo
        </button>
        <div className="toolbar-separator"></div>
        <button onClick={scrollToTop} className="toolbar-button scroll-button" title="Scroll to top">
          ↑ Top
        </button>
        <button onClick={scrollToLastDate} className="toolbar-button scroll-button" title="Scroll to last date">
          📅 Last Date
        </button>
      </div>
      
      <div className={`excel-table-wrapper ${resizingColumn !== null ? 'resizing' : ''}`} ref={tableWrapperRef}>
        <table className={`excel-table ${fixedHeaders ? 'fixed-headers' : ''}`}>
          <thead>
            {/* Totals and averages removed - shown as stats above table title instead */}
            {/* Currency totals and regular totals removed - shown as stats above table title instead */}
            <tr>
              {displayData[0]?.row?.map((header, colIndex) => {
                const isSnoColumn = isSnoColumnCheck(colIndex, header);
                const columnWidth = getColumnWidth(colIndex);
                const isDate = isDateColumn(colIndex);
                const isSorted = sortColumn === colIndex;
                const hasFilter = filters[colIndex] && filters[colIndex].trim() !== '';
                const showFilter = showFilterInput === colIndex;
                return (
                  <th 
                    key={colIndex} 
                    className={`column-header header-cell ${isSnoColumn ? 'sno-column-header' : ''}`}
                    style={isSnoColumn 
                      ? { width: '50px', minWidth: '50px', maxWidth: '50px', padding: '4px 2px' }
                      : (columnWidth ? { width: `${columnWidth}px`, minWidth: `${columnWidth}px` } : {})
                    }
                  >
                    <div className="header-content">
                      {isSnoColumn ? (
                        <div className="header-title-row" style={{ padding: 0, margin: 0, minHeight: 'auto' }}>
                          <span style={{ fontSize: '0.65rem', lineHeight: 1, padding: 0, margin: 0 }}>
                            {header === 'Sno' ? 'Sno' : header === 's no' ? 's no' : 'S no'}
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="header-title-row">
                            <span className="header-text">
                            {header || 'Column ' + (colIndex + 1)}
                          </span>
                            {isColumnDeletable(colIndex) && (
                              <button
                                className="delete-column-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteColumn(colIndex);
                                }}
                                title="Delete column"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          <div className="header-actions-row">
                            <button
                              className={`sort-button ${isSorted ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSort(colIndex);
                              }}
                              title={
                                isSorted 
                                  ? (sortDirection === 'asc' 
                                      ? 'Click to sort descending, click again to unsort' 
                                      : 'Click to unsort')
                                  : 'Click to sort ascending'
                              }
                            >
                              {isSorted ? (sortDirection === 'asc' ? '↑' : '↓') : '⇅'}
                            </button>
                            <button
                              ref={el => filterButtonRefs.current[colIndex] = el}
                              className={`filter-button ${hasFilter ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showFilter) {
                                  setShowFilterInput(null);
                                } else {
                                  // Calculate position relative to the button for portal
                                  const buttonRect = e.currentTarget.getBoundingClientRect();
                                  const viewportWidth = window.innerWidth;
                                  const viewportHeight = window.innerHeight;
                                  
                                  // Calculate optimal position - ensure it's not cut off
                                  let leftPos = buttonRect.left;
                                  let topPos = buttonRect.bottom + 4;
                                  const filterWidth = 250;
                                  
                                  // If filter would go off right edge, position it to the left of button
                                  if (leftPos + filterWidth > viewportWidth - 10) {
                                    leftPos = Math.max(10, buttonRect.right - filterWidth);
                                  }
                                  
                                  // If filter would go off bottom, position it above button
                                  if (topPos + 60 > viewportHeight - 10) {
                                    topPos = Math.max(10, buttonRect.top - 60);
                                  }
                                  
                                  setFilterPosition({
                                    top: topPos,
                                    left: leftPos,
                                    width: filterWidth
                                  });
                                  setShowFilterInput(colIndex);
                                }
                              }}
                              title={hasFilter ? "Filter active - Click to edit or use X to clear" : "Click to filter"}
                            >
                              ⚲
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    {(() => {
                      if (isSnoColumn) return null;
                      
                      // Allow resizing for Notes column even if it's the last column
                      const currentHeaderRow = displayData[0]?.row || [];
                      const header = currentHeaderRow[colIndex];
                      const isNotesColumn = header && String(header).toLowerCase().trim() === 'notes';
                      const totalCols = currentHeaderRow.length || 0;
                      const isLastColumn = colIndex === totalCols - 1;
                      
                      // Show resizer if: not last column OR it's the Notes column
                      if (isLastColumn && !isNotesColumn) {
                        return null;
                      }
                      
                      return (
                        <div
                          className="column-resizer"
                          onMouseDown={(e) => handleResizeStart(colIndex, e)}
                          title="Drag to resize column"
                        />
                      );
                    })()}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayData.slice(1).map((item, rowIndex) => {
              const row = item.row;
              const actualRowIndex = item.originalIndex;
              const rowIsEmpty = row.every((cellValue, colIndex) => {
                const headerValue = displayData[0]?.row?.[colIndex];
                if (isSnoColumnCheck(colIndex, headerValue)) return true;
                if (cellValue === null || cellValue === undefined) return true;
                if (typeof cellValue === 'string') {
                  return cellValue.trim() === '';
                }
                return cellValue === '';
              });
              return (
                <tr 
                  key={`${rowIndex}-${actualRowIndex}`}
                  className={rowIsEmpty ? 'empty-row' : ''}
                >
                  {row.map((cell, colIndex) => {
                    const headerValue = displayData[0]?.row?.[colIndex];
                    const isSnoColumn = isSnoColumnCheck(colIndex, headerValue);
                    const isSelected = isCellInSelection(actualRowIndex, colIndex);
                    const isEditing = editingCell?.row === actualRowIndex && editingCell?.col === colIndex;
                    const isCurrencyColumn = currencyColumns.includes(colIndex);
                    const isCustomDropdownColumn = customDropdownColumns && customDropdownColumns[colIndex];
                    const isDropdownColumn = isCurrencyColumn || isCustomDropdownColumn;
                    const isDropdownOpen = showDropdown?.row === actualRowIndex && showDropdown?.col === colIndex;
                    const columnWidth = getColumnWidth(colIndex);
                    const inFillRange = isInFillRange(actualRowIndex, colIndex);
                    const customDropdownOptions = isCustomDropdownColumn ? customDropdownColumns[colIndex] : [];
                    const cellStyle = isSnoColumn 
                      ? { width: '50px', minWidth: '50px', maxWidth: '50px', padding: '4px 2px' }
                      : (columnWidth ? { width: `${columnWidth}px`, minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` } : {});
                    
                    if (isDropdownOpen) {
                      cellStyle.overflow = 'visible';
                      cellStyle.zIndex = 25;
                    }
                    
                    return (
                      <td
                        key={colIndex}
                        data-row={actualRowIndex}
                        data-col={colIndex}
                        className={`excel-cell ${isSelected ? 'selected' : ''} ${isSnoColumn ? 'sno-column-cell' : ''} ${inFillRange ? 'fill-range' : ''}`}
                        style={cellStyle}
                        onMouseDown={(e) => !isSnoColumn && handleCellMouseDown(actualRowIndex, colIndex, e)}
                        onMouseEnter={() => !isSnoColumn && handleCellMouseEnter(actualRowIndex, colIndex)}
                        onMouseUp={() => !isSnoColumn && handleCellMouseUp()}
                        onClick={(e) => !isSnoColumn && handleCellClick(actualRowIndex, colIndex, e)}
                      >
                        {isSnoColumn ? (
                          <div className="row-header-content">
                            <span>{actualRowIndex}</span>
                            <button
                              className="delete-row-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteRow(actualRowIndex);
                              }}
                              title="Delete row"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        ) : isEditing ? (
                          <input
                            ref={inputRef}
                            type="text"
                            value={editValue}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            onKeyDown={handleInputKeyDown}
                            className="cell-input"
                            inputMode={(() => {
                              const headerRow = displayData[0]?.row || [];
                              const header = headerRow[colIndex];
                              const isNotesColumn = header && String(header).toLowerCase().trim() === 'notes';
                              return isNotesColumn ? 'text' : undefined;
                            })()}
                          />
                        ) : (
                          <>
                            <span style={(() => {
                              // Check if cell value is negative
                              const cellValue = cell;
                              if (cellValue !== '' && cellValue !== null && cellValue !== undefined) {
                                // Try to parse as number (handle strings with numbers, currency symbols, etc.)
                                const numValue = parseFloat(String(cellValue).replace(/[^0-9.-]/g, ''));
                                if (!isNaN(numValue) && numValue < 0) {
                                  return { color: '#dc2626' }; // Red color for negative values
                                }
                              }
                              return {};
                            })()}>{formatValueForDisplay(cell, colIndex)}</span>
                            {isSelected && !isSnoColumn && !selectionRange && (
                              <div
                                className="fill-handle"
                                onMouseDown={(e) => handleFillHandleMouseDown(e, actualRowIndex, colIndex)}
                                title="Drag to fill cells"
                              />
                            )}
                            {isDropdownColumn && (
                              <span className="dropdown-arrow">▼</span>
                            )}
                            {isDropdownOpen && (
                              <div 
                                ref={dropdownRef}
                                className="currency-dropdown"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {isCurrencyColumn ? (
                                  <>
                                    {currencyOptions.map((option) => (
                                      <div
                                        key={option}
                                        className="dropdown-option"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCurrencySelect(actualRowIndex, colIndex, option);
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                      >
                                        <span>{option}</span>
                                        {option !== 'INR' && option !== 'AED' && (
                                          <button
                                            className="remove-currency-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveCurrency(option, e);
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            title="Remove currency"
                                          >
                                            ×
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                    <div
                                      className="dropdown-option add-currency-option"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCurrencySelect(actualRowIndex, colIndex, '+');
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <span>+ Add Currency</span>
                                    </div>
                                  </>
                                ) : isCustomDropdownColumn ? (
                                  customDropdownOptions.map((option) => (
                                    <div
                                      key={option}
                                      className="dropdown-option"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCustomDropdownSelect(actualRowIndex, colIndex, option);
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <span>{option}</span>
                                    </div>
                                  ))
                                ) : null}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Currency Modal */}
      {showAddCurrencyModal && (
        <div className="modal-overlay" onClick={() => setShowAddCurrencyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Currency</h3>
            <input
              ref={currencyInputRef}
              type="text"
              value={newCurrency}
              onChange={(e) => setNewCurrency(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddCurrency();
                } else if (e.key === 'Escape') {
                  setShowAddCurrencyModal(false);
                  setNewCurrency('');
                }
              }}
              placeholder="Enter currency code (e.g., USD, EUR)"
              className="currency-input"
              maxLength={10}
            />
            <div className="modal-actions">
              <button onClick={handleAddCurrency} className="modal-button primary">
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddCurrencyModal(false);
                  setNewCurrency('');
                }}
                className="modal-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Multiple Rows Modal */}
      {showAddRowsModal && (
        <div className="modal-overlay" onClick={() => setShowAddRowsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Multiple Rows</h3>
            <input
              ref={rowsInputRef}
              type="number"
              value={rowsToAdd}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (parseInt(value, 10) > 0 && parseInt(value, 10) <= 1000)) {
                  setRowsToAdd(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddMultipleRows();
                } else if (e.key === 'Escape') {
                  setShowAddRowsModal(false);
                  setRowsToAdd('1');
                }
              }}
              placeholder="Enter number of rows (1-1000)"
              className="currency-input"
              min="1"
              max="1000"
            />
            <div className="modal-actions">
              <button onClick={handleAddMultipleRows} className="modal-button primary">
                Add Rows
              </button>
              <button
                onClick={() => {
                  setShowAddRowsModal(false);
                  setRowsToAdd('1');
                }}
                className="modal-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      
      {/* Filter Input Modal - Rendered as Portal to appear above everything */}
      {showFilterInput !== null && createPortal(
        <div 
          className="filter-input-container" 
          ref={filterInputRef}
          style={{
            position: 'fixed',
            top: `${filterPosition.top}px`,
            left: `${filterPosition.left}px`,
            width: `${filterPosition.width}px`,
            zIndex: 999999,
            transform: 'translateZ(0)',
            WebkitTransform: 'translateZ(0)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            className="filter-input"
            placeholder={isDateColumn(showFilterInput) ? "dd/mm/yyyy" : "Filter..."}
            value={filters[showFilterInput] || ''}
            onChange={(e) => handleFilterChange(showFilterInput, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowFilterInput(null);
              }
            }}
            autoFocus
          />
          <button
            className="clear-filter-btn"
            onClick={(e) => {
              e.stopPropagation();
              const hasFilter = filters[showFilterInput];
              if (hasFilter) {
                clearFilter(showFilterInput);
              } else {
                setShowFilterInput(null);
              }
            }}
            title={filters[showFilterInput] ? "Clear filter and close" : "Close filter"}
          >
            ×
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExcelTable;

